import { execFile as execFileCb } from 'node:child_process';
import { randomUUID } from 'node:crypto';
import { existsSync, readFileSync, statSync } from 'node:fs';
import path, { dirname } from 'node:path';
import { promisify } from 'node:util';
import { Command, Option } from 'commander';
import {
  appOptions,
  commonOptions,
  contextOptions,
  regionOption,
  parseContextOptions,
} from '../options.js';
import { getLogger } from '../../utils/logger.js';
import { describeAwsFailureForWarn, flattenToOneLine } from '../../local/credential-error.js';
import {
  applyRoleArnIfSet,
  AssumeRoleFailure,
  describeRejectedRoleArn,
  isIamRoleArn,
  refusedRoleArnMessage,
} from '../../utils/role-arn.js';
import { getDockerCmd } from '../../utils/docker-cmd.js';
import { CdkLocalError, withErrorHandling } from '../../utils/error-handler.js';
import { listTargets } from '../../local/target-lister.js';
import { resolveSingleTarget } from '../../local/target-picker.js';
import { Synthesizer, type SynthesisOptions } from '../../synthesis/synthesizer.js';
import { resolveApp, resolveWatchConfig } from '../config-loader.js';
import { readCdkPathOrUndefined } from '../cdk-path.js';
import { createFileWatcher, type FileWatcher } from '../../local/file-watcher.js';
import {
  classifySourceChange,
  type ReloadAssetContext,
  type ReloadVerdict,
} from '../../local/source-change-classifier.js';
import { createWatchPredicates } from './local-start-api.js';
import {
  createLocalStateProvider,
  resolveCfnFallbackRegion,
  type ExtraStateProviders,
} from './local-state-source.js';
import type { LocalStateProvider, LocalStateRecord } from '../../local/local-state-provider.js';
import type { StackInfo } from '../../synthesis/assembly-reader.js';
import {
  getEmbedConfig,
  setEmbedConfig,
  type CdkLocalEmbedConfig,
} from '../../local/embed-config.js';
import {
  AGENTCORE_A2A_PROTOCOL,
  AGENTCORE_AGUI_PROTOCOL,
  AGENTCORE_MCP_PROTOCOL,
  pickAgentCoreCandidateStack,
  resolveAgentCoreTarget,
  type AgentCoreCodeArtifact,
  type ResolvedAgentCoreRuntime,
} from '../../local/agentcore-resolver.js';
import { buildAgentCoreCodeImage } from '../../local/agentcore-code-build.js';
import { downloadAndExtractS3Bundle } from '../../local/agentcore-s3-bundle.js';
import {
  signAgentCoreInvocation,
  type SigV4Credentials,
} from '../../local/agentcore-sigv4-sign.js';
import {
  invokeAgentCore,
  waitForAgentCorePing,
  type AgentCoreInvokeResult,
} from '../../local/agentcore-client.js';
import {
  mcpInvokeOnce,
  MCP_CONTAINER_PORT,
  MCP_PATH,
  type McpInvokeResult,
  type McpJsonRpcRequest,
} from '../../local/agentcore-mcp-client.js';
import {
  a2aInvokeOnce,
  A2A_CONTAINER_PORT,
  A2A_PATH,
  type A2aInvokeResult,
  type A2aJsonRpcRequest,
} from '../../local/agentcore-a2a-client.js';
import { invokeAgentCoreWs, type AgentCoreWsResult } from '../../local/agentcore-ws-client.js';
import { createJwksCache, verifyJwtViaDiscovery } from '../../local/cognito-jwt.js';
import { resolveEnvVars, type EnvOverrideFile } from '../../local/env-resolver.js';
import {
  substituteAgainstStateAsync,
  substituteEnvVarsFromStateAsync,
  type SubstitutionContext,
} from '../../local/state-resolver.js';
import {
  derivePseudoParametersFromRegion,
  type ImageResolutionContext,
} from '../../local/intrinsic-image.js';
import {
  ensureDockerAvailable,
  pickFreePort,
  pullImage,
  removeContainer,
  runDetached,
  streamLogs,
} from '../../local/docker-runner.js';
import { buildContainerImage } from '../../local/docker-image-builder.js';
import { parseEcrUri, pullEcrImage } from '../../local/ecr-puller.js';
import {
  AssetManifestLoader,
  getDockerImageBySourceHash,
} from '../../assets/asset-manifest-loader.js';
import type { FileAsset } from '../../types/assets.js';
import { singleFlight } from '../../utils/single-flight.js';
import { resolveProfileCredentials } from './local-start-api.js';
import { buildStsClientConfig } from '../../utils/profile-resolver.js';
import {
  applyProfileCredentialsOverlay,
  assumeRoleDetail,
  resolveExecutionRoleArnFromState,
} from './local-invoke.js';
import {
  writeProfileCredentialsFile,
  type ProfileCredentialsFile,
} from './local-profile-credentials-file.js';

export interface LocalInvokeAgentCoreOptions {
  app?: string;
  output: string;
  verbose: boolean;
  region?: string;
  profile?: string;
  roleArn?: string;
  context?: string[];
  event?: string;
  eventStdin?: boolean;
  envVars?: string;
  pull: boolean;
  build: boolean;
  containerHost: string;
  /** `--platform <linux/amd64|linux/arm64>`. Defaults to AgentCore's required arm64. */
  platform: string;
  /**
   * `--ws`: use the HTTP-protocol agent's bidirectional `/ws` WebSocket
   * endpoint (on 8080) instead of `POST /invocations` — send `--event` as the
   * first frame and stream received frames to stdout until the agent closes.
   *
   * When stdin is a TTY (interactive terminal), additionally read stdin lines
   * as follow-up text frames (one frame per line, trailing newline stripped)
   * until stdin EOFs (Ctrl-D) or the agent closes the connection. This
   * matches the deployed `/ws` endpoint's bidirectional shape: a TTY user
   * gets a multi-turn session by default. When stdin is NOT a TTY (piped /
   * redirected / CI), only the initial `--event` frame is sent and the
   * client only receives — the standard one-shot script-friendly shape.
   * Force one-shot in a TTY with `cdkl ... --ws </dev/null`.
   */
  ws?: boolean;
  /** Session id forwarded via the AgentCore session-id header (auto-generated when omitted). */
  sessionId?: string;
  /**
   * Bearer JWT to present when the runtime declares a `customJwtAuthorizer`.
   * Verified against the runtime's OIDC discovery URL before the container
   * starts, then forwarded to `/invocations` as `Authorization: Bearer <jwt>`.
   */
  bearerToken?: string;
  /**
   * Commander maps `--no-verify-auth` to `verifyAuth: boolean` (default
   * `true`). When `false`, skip inbound JWT verification entirely (local-dev
   * escape hatch) — the token, if any, is still forwarded.
   */
  verifyAuth: boolean;
  /**
   * `--sigv4`: sign the `/invocations` POST with AWS SigV4 (service
   * `bedrock-agentcore`) using the resolved credentials — matching the
   * cloud's default IAM-auth behavior when the runtime declares no
   * `customJwtAuthorizer`. The local agent receives the same `Authorization:
   * AWS4-HMAC-SHA256 ...` + `X-Amz-Date` / `X-Amz-Content-Sha256` /
   * `X-Amz-Security-Token` headers it would in the cloud. Opt-in: default
   * unsigned (preserves existing behavior). Mutually exclusive with
   * `--bearer-token` and ignored on a JWT-protected runtime.
   */
  sigv4?: boolean;
  /**
   * Optional execution role to assume before invoking. Commander's `[arn]`
   * maps to `string | boolean`:
   *   - absent → `undefined` (dev creds pass through; SAM-compatible default)
   *   - `--assume-role` (bare) → `true` (use the runtime's literal RoleArn)
   *   - `--assume-role <arn>` → `'<arn>'`
   *   - `--no-assume-role` → `false`
   */
  assumeRole?: string | boolean;
  /** Role ARN to assume before authenticating against ECR for the container image pull. */
  ecrRoleArn?: string;
  fromCfnStack?: string | boolean;
  stackRegion?: string;
  /**
   * Per-request timeout in milliseconds, applied to the HTTP `/invocations`
   * POST, the MCP `POST /mcp` request, and the `/ws` open-to-close window.
   * Default 120000 (120s). Raise this for long-running agent calls that
   * exceed the default — the cloud's AgentCore quota goes well above 120s.
   */
  timeout: number;
  /**
   * Issue #255 — `cdkl invoke-agentcore --watch`. Re-synth and reload the
   * agent container on CDK source changes. Only meaningful with the
   * long-running `--ws` session path: the single-shot `POST /invocations`
   * / `POST /mcp` / A2A `POST /`
   * paths run once and exit, so `--watch` is logged as a no-op WARN
   * there and the single-shot proceeds normally.
   *
   * Per-firing the source-change classifier (shared with `start-service`
   * / `start-alb` Phase 4 of issue #214) decides `'rebuild'` vs
   * `'soft-reload'`. Source-only edits on an interpreted-language
   * handler (Node / Python / Ruby / shell — no Dockerfile, no
   * dependency manifest, no compiled-language source) `docker cp` the
   * freshly-synthed asset directory contents into the running
   * container's WORKDIR + `docker restart`. Everything else (Dockerfile
   * / dependency manifest / compiled source / ambiguous) rebuilds the
   * image from scratch + restarts the container. The active `/ws`
   * socket is closed cleanly on every reload firing (no protocol-defined
   * mid-session container handoff) so the next session connects to the
   * rebuilt container — that is the honest local-dev semantic.
   */
  watch?: boolean;
  /** Host-injected extra state-source flag fields. */
  [key: string]: unknown;
}

/**
 * Parser for `--timeout <ms>`. Accepts a positive integer; rejects 0,
 * negatives, fractions, and non-numeric input.
 */
export function parseTimeoutMs(raw: string): number {
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new CdkLocalError(
      `--timeout must be a positive integer number of milliseconds (got '${raw}').`,
      'LOCAL_INVOKE_AGENTCORE_TIMEOUT_INVALID'
    );
  }
  return parsed;
}

/**
 * Factory options for {@link createLocalInvokeAgentCoreCommand}.
 */
export interface CreateLocalInvokeAgentCoreCommandOptions {
  extraStateProviders?: ExtraStateProviders;
  /** Embed-time branding overrides for a host wrapping this factory. */
  embedConfig?: CdkLocalEmbedConfig;
}

/**
 * `cdkl invoke-agentcore <target>` — run a Bedrock AgentCore Runtime container
 * locally and invoke it once over the AgentCore HTTP contract. Resolves
 * the `AWS::BedrockAgentCore::Runtime`, pulls / builds its container,
 * starts it on port 8080, waits for `GET /ping`, POSTs the event to
 * `POST /invocations`, prints the response, and tears down. Covers the
 * container artifact and the CodeConfiguration managed-runtime artifact
 * (fromCodeAsset, built from source) on the HTTP + MCP protocols; the agent's
 * calls to real AWS go to real AWS (credentials injected like `cdkl invoke`).
 */
async function localInvokeAgentCoreCommand(
  target: string | undefined,
  options: LocalInvokeAgentCoreOptions,
  extraStateProviders: ExtraStateProviders | undefined
): Promise<void> {
  const logger = getLogger();
  if (options.verbose) logger.setLevel('debug');

  let containerId: string | undefined;
  let stopLogs: (() => void) | undefined;
  let sigintHandler: (() => void) | undefined;
  let profileCredsFile: ProfileCredentialsFile | undefined;
  let stateProvider: LocalStateProvider | undefined;

  const cleanup = singleFlight(
    async (): Promise<void> => {
      if (stateProvider) {
        try {
          stateProvider.dispose();
        } catch (err) {
          getLogger().debug(
            `state provider dispose failed: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }
      if (stopLogs) {
        try {
          stopLogs();
        } catch (err) {
          getLogger().debug(
            `streamLogs stop failed: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }
      if (containerId) {
        try {
          await removeContainer(containerId);
        } catch (err) {
          getLogger().debug(
            `removeContainer(${containerId}) failed: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }
      if (profileCredsFile) {
        try {
          await profileCredsFile.dispose();
        } catch (err) {
          getLogger().debug(
            `Failed to remove profile credentials tmpdir ${profileCredsFile.hostPath}: ${
              err instanceof Error ? err.message : String(err)
            }`
          );
        }
      }
    },
    (err) => {
      getLogger().debug(`cleanup failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  );

  try {
    await applyRoleArnIfSet({
      roleArn: options.roleArn,
      region: options.region,
      profile: options.profile,
    });
    await ensureDockerAvailable();

    const profileCredentials = options.profile
      ? await resolveProfileCredentials(options.profile)
      : undefined;
    if (options.profile && profileCredentials) {
      profileCredsFile = await writeProfileCredentialsFile(options.profile, profileCredentials);
    }

    const appCmd = resolveApp(options.app);
    if (!appCmd) {
      throw new Error(
        `No CDK app specified. Pass --app, set ${getEmbedConfig().envPrefix}_APP, or add "app" to cdk.json.`
      );
    }

    logger.info('Synthesizing CDK app...');
    const synthesizer = new Synthesizer();
    const context = parseContextOptions(options.context);
    const synthOpts: SynthesisOptions = {
      app: appCmd,
      output: options.output,
      ...(options.region && { region: options.region }),
      ...(options.profile && { profile: options.profile }),
      ...(Object.keys(context).length > 0 && { context }),
    };
    const { stacks } = await synthesizer.synthesize(synthOpts);

    const resolvedTarget = await resolveSingleTarget(target, {
      entries: listTargets(stacks).agentCoreRuntimes,
      message: 'Select an AgentCore Runtime to invoke',
      noun: 'AgentCore Runtimes',
      onMissing: () =>
        new CdkLocalError(
          `${getEmbedConfig().cliName} invoke-agentcore requires a <target> (an AgentCore Runtime display path or logical ID). ` +
            `Run \`${getEmbedConfig().cliName} list\` to see them, or run it in a TTY to pick interactively.`,
          'LOCAL_INVOKE_AGENTCORE_TARGET_REQUIRED'
        ),
    });

    // Build a `--from-cfn-stack` image-resolution context BEFORE resolving the
    // target, so a same-stack AWS::ECR::Repository Fn::Join ContainerUri (or an
    // Fn::Sub asset URI) reduces to the deployed image URI. The state load is
    // shared with the env-substitution + role-from-state steps below.
    const candidate = pickAgentCoreCandidateStack(resolvedTarget, stacks);
    stateProvider = createLocalStateProvider(
      options,
      candidate?.stackName ?? '',
      await resolveCfnFallbackRegion(options, candidate?.region),
      extraStateProviders
    );
    const { context: imageContext, loaded: loadedState } =
      stateProvider && candidate
        ? await buildAgentCoreImageContext(candidate, stateProvider, options)
        : { context: undefined, loaded: undefined };

    const resolved = resolveAgentCoreTarget(resolvedTarget, stacks, imageContext);
    logger.info(`Target: ${resolved.stack.stackName}/${resolved.logicalId} (${resolved.protocol})`);
    const isMcp = resolved.protocol === AGENTCORE_MCP_PROTOCOL;
    const isA2a = resolved.protocol === AGENTCORE_A2A_PROTOCOL;
    const isAgui = resolved.protocol === AGENTCORE_AGUI_PROTOCOL;
    if (isAgui) {
      // AG-UI's wire shape is HTTP-compatible: SSE on `POST /invocations` and a
      // bidirectional WebSocket on `/ws`, both on port 8080. So an AG-UI
      // runtime routes through the same client path as HTTP — the existing
      // SSE / WS handlers stream bytes transparently. Surface this in the
      // log so users aren't surprised the AGUI runtime "becomes" HTTP.
      logger.info(
        'AGUI runtime: routing through the HTTP /invocations + /ws path (AG-UI wire is SSE / WebSocket on port 8080).'
      );
    }
    if ((isMcp || isA2a) && options.ws) {
      logger.warn(
        `--ws applies only to the HTTP / AGUI protocols; ignoring it for this ${resolved.protocol} runtime.`
      );
    }
    if (options.sigv4 && (isMcp || isA2a || options.ws)) {
      logger.warn(
        '--sigv4 signs the HTTP /invocations request only; ignoring it for the ' +
          (isMcp ? 'MCP' : isA2a ? 'A2A' : '/ws WebSocket') +
          ' path.'
      );
    }
    // Issue #255 — `--watch` is only meaningful on the long-running /ws
    // session paths. The single-shot HTTP `POST /invocations`, MCP `POST /mcp`,
    // and A2A `POST /` paths run once and exit, so a watch loop has nothing
    // to drive: log a one-line WARN and let the single shot proceed normally
    // (the flag declaration itself does not gate that case).
    const watchEligible = options.ws === true && !isMcp && !isA2a;
    const watchActive = options.watch === true && watchEligible;
    if (options.watch === true && !watchEligible) {
      const ignoredFor = isMcp ? 'MCP' : isA2a ? 'A2A' : 'single-shot HTTP POST /invocations';
      logger.warn(
        `--watch is meaningful only with the long-running --ws session path; ` +
          `ignoring it for the ${ignoredFor} path (single-shot invocations run once and exit).`
      );
    }

    // Read + validate the event (and resolve the session id) BEFORE any
    // Docker work, so a bad --event / --event-stdin fails fast instead of
    // after paying for an image build + container boot.
    const sessionId = options.sessionId ?? randomUUID();
    const event = await readEvent(options);
    // For MCP, parse the event into a JSON-RPC request up front so a malformed
    // one fails fast too (default: tools/list).
    const mcpRequest = isMcp ? buildMcpRequest(event) : undefined;
    // A2A is JSON-RPC 2.0 too: parse the event up front into a method/params
    // (default: agent/getCard — the agent's discovery card).
    const a2aRequest = isA2a ? buildA2aRequest(event) : undefined;

    // Inbound JWT auth: when the runtime declares a customJwtAuthorizer,
    // verify the supplied bearer token against its OIDC discovery URL BEFORE
    // any Docker work — rejecting a missing / invalid token the way AgentCore
    // does. Returns the `Authorization` header to forward to the container.
    // MCP / A2A talk vanilla JSON-RPC directly to the container; their
    // bearer / session is an AgentCore managed-plane concern the front door
    // layers on top, so it is not applied to a direct local POST.
    let authorization: string | undefined;
    if (isMcp || isA2a) {
      if (resolved.jwtAuthorizer || options.bearerToken) {
        const pathLabel = isMcp ? MCP_PATH : A2A_PATH;
        logger.info(
          `${resolved.protocol} runtime: invoking the local container's ${pathLabel} directly (vanilla ${resolved.protocol}). ` +
            `An inbound JWT / --bearer-token is an AgentCore managed-plane concern and is not applied locally.`
        );
      }
    } else {
      authorization = await resolveInboundAuthorization(resolved, options);
    }

    // If the fromS3 bundle's Code.S3.Bucket is an intrinsic (Ref /
    // Fn::ImportValue / Fn::GetStackOutput), resolve it against --from-cfn-stack
    // state BEFORE the image step (which needs a literal bucket for the S3
    // download). Reuses the same substitution machinery env vars use, so
    // every cross-stack intrinsic the env path supports is supported here too.
    await resolveFromS3BucketIntrinsic(resolved, stateProvider, loadedState, imageContext);

    const image = await resolveAgentCoreImage(resolved, options, loadedState, stateProvider);

    const { env: dockerEnv, sensitiveEnvKeys } = await buildContainerEnv(
      resolved,
      options,
      profileCredentials,
      profileCredsFile,
      stateProvider,
      loadedState,
      imageContext
    );

    const hostPort = await pickFreePort();
    const containerHost = options.containerHost;
    // Stable `cdkl-`-prefixed name so the orphan sweep (`docker ps --filter
    // name=cdkl-`) used by `/cleanup` + `/run-integ` can find this container
    // if the process is killed before teardown — unlike a one-shot Lambda
    // invoke, the agent container runs a long-lived HTTP server.
    const containerName = `${getEmbedConfig().resourceNamePrefix}-agentcore-${process.pid}-${Math.random().toString(36).slice(2, 8)}`;
    const containerPort = isMcp ? MCP_CONTAINER_PORT : isA2a ? A2A_CONTAINER_PORT : undefined;
    const containerPortLabel = isMcp
      ? `${MCP_CONTAINER_PORT}${MCP_PATH}`
      : isA2a
        ? `${A2A_CONTAINER_PORT}${A2A_PATH}`
        : '8080';
    logger.info(
      `Starting agent container (image=${image}, port=${hostPort} -> ${containerPortLabel})...`
    );
    containerId = await runDetached({
      image,
      mounts: [],
      env: dockerEnv,
      cmd: [],
      hostPort,
      host: containerHost,
      platform: options.platform,
      name: containerName,
      ...(containerPort !== undefined && { containerPort }),
      // Keep decrypted SecureString SSM env values off the `docker run` argv.
      ...(sensitiveEnvKeys.size > 0 && { sensitiveEnvKeys }),
    });

    stopLogs = streamLogs(containerId);

    sigintHandler = (): void => {
      void cleanup().then(() => process.exit(130));
    };
    process.on('SIGINT', sigintHandler);

    if (isMcp && mcpRequest) {
      // MCP has no /ping: mcpInvokeOnce folds the boot-wait into a retried
      // `initialize`, then runs the session handshake + the one request.
      logger.info(`MCP request: ${mcpRequest.method}`);
      const mcp = await mcpInvokeOnce(containerHost, hostPort, mcpRequest, {
        requestTimeoutMs: options.timeout,
      });
      // Settle so container logs flush before teardown.
      await new Promise((r) => setTimeout(r, 250));
      emitMcpResult(mcp);
    } else if (isA2a && a2aRequest) {
      // A2A has no /ping either: a2aInvokeOnce folds the boot-wait into a
      // retried POST. One JSON-RPC round-trip — vanilla A2A.
      logger.info(`A2A request: ${a2aRequest.method}`);
      const a2a = await a2aInvokeOnce(containerHost, hostPort, a2aRequest, {
        requestTimeoutMs: options.timeout,
      });
      await new Promise((r) => setTimeout(r, 250));
      emitA2aResult(a2a);
    } else if (options.ws) {
      // Bidirectional `/ws` (same 8080 container as /invocations): send the
      // event as the first frame, stream every received frame to stdout, then
      // resolve when the agent closes the stream. When stdin is a TTY
      // (real terminal — `process.stdin.isTTY === true`), additionally wire
      // `process.stdin` (line-buffered) as a frame source so each typed line
      // becomes a follow-up text frame until EOF (Ctrl-D) or the agent
      // closes — the multi-turn shape the deployed `/ws` endpoint is built
      // for. When stdin is NOT a TTY (piped, redirected, CI), only the
      // initial event frame is sent and the client receives until close —
      // the standard one-shot script-friendly shape. Force one-shot in a
      // TTY with `cdkl ... --ws </dev/null`.
      const interactive = process.stdin.isTTY === true;
      if (watchActive) {
        // Issue #255 — `--watch` wraps the /ws dispatch in a reload-driven
        // loop. The WebSocket is closed cleanly on every reload firing
        // (via the abort signal) and re-opened against the rebuilt /
        // soft-reloaded container; the host-side teardown is otherwise
        // identical to the non-watch path. The watch loop only returns
        // when SIGINT triggers cleanup.
        await runAgentCoreWatchLoop({
          containerHost,
          hostPort,
          event,
          sessionId,
          timeoutMs: options.timeout,
          interactive,
          options,
          resolvedTarget,
          resolved,
          synthesizer,
          synthOpts,
          stacks,
          ...(authorization && { authorization }),
          rebuild: async () => {
            // Tear down the OLD container, then re-run the full image build
            // + container run pipeline against the new synth. Returns the
            // new (containerId, stopLogs, hostPort). Reuses the same
            // env / state / image-resolution machinery used at first
            // boot; the only thing not re-resolved is the inbound auth
            // (the bearer token / OIDC discovery URL is stable across
            // a CDK source edit).
            if (stopLogs) {
              try {
                stopLogs();
              } catch {
                /* best-effort */
              }
              stopLogs = undefined;
            }
            if (containerId) {
              await removeContainer(containerId);
              containerId = undefined;
            }
            const { stacks: newStacks } = await synthesizer.synthesize(synthOpts);
            const newCandidate = pickAgentCoreCandidateStack(resolvedTarget, newStacks);
            const { context: newImageContext, loaded: newLoaded } =
              stateProvider && newCandidate
                ? await buildAgentCoreImageContext(newCandidate, stateProvider, options)
                : { context: undefined, loaded: undefined };
            const newResolved = resolveAgentCoreTarget(resolvedTarget, newStacks, newImageContext);
            await resolveFromS3BucketIntrinsic(
              newResolved,
              stateProvider,
              newLoaded,
              newImageContext
            );
            const newImage = await resolveAgentCoreImage(
              newResolved,
              options,
              newLoaded,
              stateProvider
            );
            const { env: newEnv, sensitiveEnvKeys: newSensitive } = await buildContainerEnv(
              newResolved,
              options,
              profileCredentials,
              profileCredsFile,
              stateProvider,
              newLoaded,
              newImageContext
            );
            const newHostPort = await pickFreePort();
            const newName = `${getEmbedConfig().resourceNamePrefix}-agentcore-${process.pid}-${Math.random().toString(36).slice(2, 8)}`;
            logger.info(
              `Reload: starting agent container (image=${newImage}, port=${newHostPort} -> 8080)...`
            );
            containerId = await runDetached({
              image: newImage,
              mounts: [],
              env: newEnv,
              cmd: [],
              hostPort: newHostPort,
              host: containerHost,
              platform: options.platform,
              name: newName,
              ...(newSensitive.size > 0 && { sensitiveEnvKeys: newSensitive }),
            });
            stopLogs = streamLogs(containerId);
            return { containerId, hostPort: newHostPort, stacks: newStacks };
          },
          softReload: async (newSourceDir) => {
            if (!containerId) {
              throw new CdkLocalError(
                'softReload: no live container to docker cp / docker restart into.',
                'LOCAL_INVOKE_AGENTCORE_WATCH_NO_CONTAINER'
              );
            }
            await softReloadAgentContainer(containerId, newSourceDir);
            // Re-synth so the caller has the freshly-resolved stacks for the
            // next classifier firing's asset-context lookup; we discard env /
            // image changes (soft-reload by definition only swaps source).
            const { stacks: newStacks } = await synthesizer.synthesize(synthOpts);
            return { stacks: newStacks };
          },
        });
      } else {
        await waitForAgentCorePing(containerHost, hostPort);
        const frameSource = interactive ? readStdinLines() : undefined;
        logger.info(
          interactive
            ? 'Opening the agent /ws WebSocket (interactive — stdin lines = follow-up frames; Ctrl-D to end)...'
            : 'Opening the agent /ws WebSocket and streaming frames...'
        );
        const wsResult = await invokeAgentCoreWs(containerHost, hostPort, event, {
          sessionId,
          timeoutMs: options.timeout,
          onMessage: wrapWsOnMessage((text) => process.stdout.write(text), interactive),
          ...(authorization && { authorization }),
          ...(frameSource && { frameSource }),
        });
        // Settle so container logs flush before teardown.
        await new Promise((r) => setTimeout(r, 250));
        emitWsResult(wsResult);
      }
    } else {
      await waitForAgentCorePing(containerHost, hostPort);

      // `--sigv4` opt-in: sign the request with the resolved host credentials
      // (service `bedrock-agentcore`) so the agent receives the same
      // Authorization + X-Amz-* headers it would in the cloud. Mutually
      // exclusive with `--bearer-token`; ignored when a customJwtAuthorizer is
      // declared (the JWT path takes precedence).
      const additionalHeaders = await buildSigV4HeadersIfRequested(
        options,
        resolved,
        loadedState,
        containerHost,
        hostPort,
        event,
        sessionId,
        stateProvider
      );

      const result = await invokeAgentCore(containerHost, hostPort, event, {
        sessionId,
        timeoutMs: options.timeout,
        // Stream a text/event-stream response to stdout as it arrives, so a
        // token-streaming agent shows incrementally rather than all at once.
        onChunk: (text) => process.stdout.write(text),
        ...(authorization && { authorization }),
        ...(additionalHeaders && { additionalHeaders }),
      });

      // Settle so container logs flush before teardown.
      await new Promise((r) => setTimeout(r, 250));
      emitResult(result);
    }
  } finally {
    if (sigintHandler) process.off('SIGINT', sigintHandler);
    await cleanup();
  }
}

/**
 * Enforce the runtime's inbound JWT authorizer (when declared) and return
 * the `Authorization` header to forward to `/invocations`.
 *
 * - No authorizer → forward the token verbatim if one was given (no-op
 *   otherwise).
 * - `--no-verify-auth` → warn + forward without verifying (local-dev escape).
 * - Authorizer + no token → reject (AgentCore returns 401).
 * - Authorizer + token → verify against the OIDC discovery URL; reject on
 *   failure (AgentCore returns 403); forward on success. An unreachable
 *   discovery URL falls back to pass-through accept (offline-dev fallback in
 *   {@link verifyJwtViaDiscovery}).
 *
 * Exported so a unit test can drive the gate without the full Docker pipeline.
 */
export async function resolveInboundAuthorization(
  resolved: ResolvedAgentCoreRuntime,
  options: { bearerToken?: string; verifyAuth: boolean }
): Promise<string | undefined> {
  const logger = getLogger();
  const authorizer = resolved.jwtAuthorizer;
  const header = options.bearerToken ? `Bearer ${options.bearerToken}` : undefined;

  if (!authorizer) return header;

  if (options.verifyAuth === false) {
    logger.warn(
      `Runtime '${resolved.logicalId}' declares a customJwtAuthorizer, but --no-verify-auth was set — ` +
        `skipping inbound JWT verification (local-dev escape hatch).`
    );
    return header;
  }

  if (!header) {
    throw new CdkLocalError(
      `Runtime '${resolved.logicalId}' requires an inbound JWT (customJwtAuthorizer). ` +
        `Pass --bearer-token <jwt>, or --no-verify-auth to skip verification for local dev.`,
      'LOCAL_INVOKE_AGENTCORE_AUTH_REQUIRED'
    );
  }

  const result = await verifyJwtViaDiscovery(
    {
      discoveryUrl: authorizer.discoveryUrl,
      ...(authorizer.allowedAudience && { allowedAudience: authorizer.allowedAudience }),
      ...(authorizer.allowedClients && { allowedClients: authorizer.allowedClients }),
      ...(authorizer.allowedScopes && { allowedScopes: authorizer.allowedScopes }),
      ...(authorizer.customClaims && { customClaims: authorizer.customClaims }),
    },
    header,
    createJwksCache(),
    { warnedAt: new Map() }
  );
  if (!result.allow) {
    throw new CdkLocalError(
      `Inbound JWT rejected by the runtime's customJwtAuthorizer ` +
        `(signature / issuer / expiry / audience check failed against ${authorizer.discoveryUrl}).`,
      'LOCAL_INVOKE_AGENTCORE_AUTH_DENIED'
    );
  }
  logger.info(`Inbound JWT verified against ${authorizer.discoveryUrl}.`);
  return header;
}

/**
 * Resolve the boot-time SigV4 signing context (`{ credentials, region }`) when
 * `--sigv4` is requested, or `undefined` when it is not applicable (`--sigv4`
 * off, or a `customJwtAuthorizer` is declared — the JWT path wins, warns).
 * Throws a {@link CdkLocalError} on a `--bearer-token` conflict, an unresolved
 * region, or no resolvable credentials.
 *
 * Extracted from {@link buildSigV4HeadersIfRequested} so both the single-shot
 * `cdkl invoke-agentcore` (signs ONE body) and the warm `cdkl start-agentcore`
 * serve (signs each forwarded body per request — issue #454) resolve the
 * credentials + region the SAME way. Exported so the serve boot + a unit test
 * can reuse it.
 */
export async function resolveAgentCoreSigV4Context(
  options: LocalInvokeAgentCoreOptions,
  resolved: ResolvedAgentCoreRuntime,
  loaded: LocalStateRecord | undefined,
  stateProvider?: LocalStateProvider
): Promise<{ credentials: SigV4Credentials; region: string } | undefined> {
  if (!options.sigv4) return undefined;
  if (options.bearerToken) {
    throw new CdkLocalError(
      `--sigv4 and --bearer-token are mutually exclusive: pick one inbound auth.`,
      'LOCAL_INVOKE_AGENTCORE_AUTH_CONFLICT'
    );
  }
  if (resolved.jwtAuthorizer) {
    getLogger().warn(
      `Runtime '${resolved.logicalId}' declares a customJwtAuthorizer; --sigv4 ignored (JWT path takes precedence).`
    );
    return undefined;
  }
  const region =
    options.region ??
    options.stackRegion ??
    process.env['AWS_REGION'] ??
    process.env['AWS_DEFAULT_REGION'] ??
    resolved.stack.region;
  if (!region) {
    throw new CdkLocalError(
      `--sigv4: no region resolved for the AgentCore signing scope. ` +
        `Pass --region <region>, set AWS_REGION, or use --from-cfn-stack with a region-bound stack.`,
      'LOCAL_INVOKE_AGENTCORE_SIGV4_NO_REGION'
    );
  }
  const credentials = await resolveHostCredentialsForSigV4(
    options,
    resolved,
    loaded,
    region,
    stateProvider
  );
  return { credentials, region };
}

/**
 * Compute the SigV4 headers for the `/invocations` POST when `--sigv4` is
 * requested. Returns `undefined` (no header overlay) when:
 *
 * - `--sigv4` is not set,
 * - the runtime declares a `customJwtAuthorizer` (the JWT path wins; warns),
 *
 * Throws a {@link CdkLocalError} when `--sigv4` conflicts with
 * `--bearer-token`, or when no AWS credentials are resolvable for signing.
 *
 * Exported so a unit test can drive the gate without the full Docker pipeline.
 */
export async function buildSigV4HeadersIfRequested(
  options: LocalInvokeAgentCoreOptions,
  resolved: ResolvedAgentCoreRuntime,
  loaded: LocalStateRecord | undefined,
  host: string,
  port: number,
  event: unknown,
  sessionId: string,
  stateProvider?: LocalStateProvider
): Promise<Record<string, string> | undefined> {
  const ctx = await resolveAgentCoreSigV4Context(options, resolved, loaded, stateProvider);
  if (!ctx) return undefined;
  const signed = await signAgentCoreInvocation({
    credentials: ctx.credentials,
    region: ctx.region,
    host,
    port,
    path: '/invocations',
    body: JSON.stringify(event ?? {}),
    sessionId,
  });
  const headers: Record<string, string> = {
    Authorization: signed.authorization,
    'X-Amz-Date': signed.amzDate,
    'X-Amz-Content-Sha256': signed.amzContentSha256,
  };
  if (signed.amzSecurityToken) headers['X-Amz-Security-Token'] = signed.amzSecurityToken;
  getLogger().info(`Signed /invocations with SigV4 (region=${ctx.region}).`);
  return headers;
}

/**
 * Resolve credentials for host-side SigV4 signing. Precedence:
 *   1. `--assume-role` → STS temp creds (warn + fall through on STS failure);
 *   2. `--profile` → profile creds (sessionToken when the profile carries one);
 *   3. shell env (`AWS_ACCESS_KEY_ID` / `AWS_SECRET_ACCESS_KEY` / optional
 *      `AWS_SESSION_TOKEN`).
 *
 * Throws a {@link CdkLocalError} when none are available — `--sigv4` cannot
 * proceed without credentials, unlike the unsigned path.
 *
 * Exported so the issue #570 site-level test can drive the STS failure
 * branch with a mocked STS client -- the same reason
 * `applyAgentCoreCredentialEnv` below is exported. A helper being correct
 * says nothing about whether a given site actually routes through it.
 */
export async function resolveHostCredentialsForSigV4(
  options: LocalInvokeAgentCoreOptions,
  resolved: ResolvedAgentCoreRuntime,
  loaded: LocalStateRecord | undefined,
  region: string,
  stateProvider: LocalStateProvider | undefined
): Promise<SigV4Credentials> {
  const logger = getLogger();
  const assumeRoleArn = await resolveAssumeRoleArn(options, resolved, loaded, stateProvider);
  if (assumeRoleArn) {
    try {
      return await assumeAgentCoreExecutionRole(assumeRoleArn, region, options.profile);
    } catch (err) {
      // Issue #570: not on the issue's list of six, but the same class --
      // see `describeAwsFailureForWarn` in `src/local/credential-error.ts`.
      // `assumeRoleArn` keeps printing for the reason recorded at the
      // `applyAgentCoreCredentialEnv` site below.
      // Issue #579: `assumeAgentCoreExecutionRole` renders its own
      // no-usable-credentials sentence, so re-rendering it here would WITHHOLD
      // cdk-local's own text (a non-service error is the withheld branch by
      // design). That split now lives once in `assumeRoleDetail`.
      //
      // This used to say the site was inlined BECAUSE one level of indirection
      // defeated the source fence. True when written, and no longer: issue
      // #644 taught `sts-error-relay-source-fence.test.ts` to collect a named
      // renderer defined in `src/cli/commands/*.ts` and accept a site that
      // delegates to it, which is what made this refactor possible.
      const sigv4Detail = assumeRoleDetail(err, 'STS AssumeRole (--sigv4 signing)');
      logger.warn(
        `--assume-role: STS AssumeRole(${flattenToOneLine(assumeRoleArn)}) failed for --sigv4 signing: ` +
          `${sigv4Detail}. ` +
          `Falling back to ${options.profile ? `--profile ${options.profile}` : 'shell credentials'}.`
      );
    }
  }
  if (options.profile) {
    const creds = await resolveProfileCredentials(options.profile);
    if (creds?.accessKeyId && creds.secretAccessKey) {
      return {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        ...(creds.sessionToken && { sessionToken: creds.sessionToken }),
      };
    }
  }
  const accessKeyId = process.env['AWS_ACCESS_KEY_ID'];
  const secretAccessKey = process.env['AWS_SECRET_ACCESS_KEY'];
  if (accessKeyId && secretAccessKey) {
    const sessionToken = process.env['AWS_SESSION_TOKEN'];
    return {
      accessKeyId,
      secretAccessKey,
      ...(sessionToken && { sessionToken }),
    };
  }
  throw new CdkLocalError(
    `--sigv4: no AWS credentials available to sign the request. ` +
      `Set AWS_ACCESS_KEY_ID + AWS_SECRET_ACCESS_KEY, pass --profile <name>, or pass --assume-role <arn>.`,
    'LOCAL_INVOKE_AGENTCORE_SIGV4_NO_CREDENTIALS'
  );
}

/**
 * Acquire the agent image. A CODE artifact (managed runtime) is built from
 * source — a fromCodeAsset bundle from its cdk.out asset, a fromS3 bundle
 * downloaded + extracted from S3. A CONTAINER artifact mirrors the
 * container-Lambda path: build from a local cdk.out asset when the URI matches
 * one, else pull from ECR, else pull a plain registry image.
 *
 * `loaded` is the `--from-cfn-stack` state record (when available) — threaded
 * through so a bare `--assume-role` can resolve the execution-role ARN from
 * state for the fromS3 download. `stateProvider` enables the issue-#187
 * live-fallback to `bedrock-agentcore-control:GetAgentRuntime` when the
 * static state lookup misses.
 */
export async function resolveAgentCoreImage(
  resolved: ResolvedAgentCoreRuntime,
  options: LocalInvokeAgentCoreOptions,
  loaded?: LocalStateRecord,
  stateProvider?: LocalStateProvider
): Promise<string> {
  const logger = getLogger();
  const architecture = platformToArchitecture(options.platform);

  if (resolved.codeArtifact) {
    return resolveAgentCoreCodeImage(
      resolved,
      resolved.codeArtifact,
      options,
      architecture,
      loaded,
      stateProvider
    );
  }

  const containerUri = resolved.containerUri;
  if (containerUri === undefined) {
    throw new CdkLocalError(
      `AgentCore Runtime '${resolved.logicalId}' has neither a container image nor a code artifact to run.`,
      'LOCAL_INVOKE_AGENTCORE_NO_ARTIFACT'
    );
  }

  const manifestPath = resolved.stack.assetManifestPath;
  if (manifestPath) {
    const cdkOutDir = dirname(manifestPath);
    const loader = new AssetManifestLoader();
    const manifest = await loader.loadManifest(cdkOutDir, resolved.stack.stackName);
    if (manifest) {
      const entry = getDockerImageBySourceHash(manifest, containerUri);
      if (entry) {
        return buildContainerImage(entry.asset, cdkOutDir, {
          architecture,
          noBuild: options.build === false,
        });
      }
    }
  }

  if (parseEcrUri(containerUri)) {
    logger.info(`Pulling agent image from ECR: ${containerUri}`);
    return pullEcrImage(containerUri, {
      skipPull: options.pull === false,
      ...(options.region !== undefined && { region: options.region }),
      ...(options.ecrRoleArn !== undefined && { ecrRoleArn: options.ecrRoleArn }),
      ...(options.profile !== undefined && { profile: options.profile }),
    });
  }

  await pullImage(containerUri, options.pull === false);
  return containerUri;
}

/**
 * Build a local image from a `CodeConfiguration` (managed-runtime) bundle.
 *
 * - fromS3 (`code.s3Source` set, a literal S3 object): download + extract the
 *   bundle, then run the from-source build over the extracted dir.
 * - fromCodeAsset: locate the source dir in cdk.out via its asset hash, then
 *   run the same from-source build (generated Dockerfile → install deps → run
 *   EntryPoint).
 */
async function resolveAgentCoreCodeImage(
  resolved: ResolvedAgentCoreRuntime,
  code: AgentCoreCodeArtifact,
  options: LocalInvokeAgentCoreOptions,
  architecture: 'x86_64' | 'arm64',
  loaded?: LocalStateRecord,
  stateProvider?: LocalStateProvider
): Promise<string> {
  if (code.s3Source) {
    return resolveAgentCoreCodeImageFromS3(
      resolved,
      code,
      code.s3Source,
      options,
      architecture,
      loaded,
      stateProvider
    );
  }

  const manifestPath = resolved.stack.assetManifestPath;
  if (!manifestPath) {
    throw new CdkLocalError(
      `AgentCore Runtime '${resolved.logicalId}' uses a code artifact, but its stack has no asset ` +
        `manifest in cdk.out to read the bundle source from.`,
      'LOCAL_INVOKE_AGENTCORE_CODE_NO_MANIFEST'
    );
  }
  const cdkOutDir = dirname(manifestPath);
  const loader = new AssetManifestLoader();
  const manifest = await loader.loadManifest(cdkOutDir, resolved.stack.stackName);
  const fileAssets = manifest ? loader.getFileAssets(manifest) : undefined;
  // The manifest's `files` are keyed by SOURCE hash; for the default
  // synthesizer that equals the destination objectKey hash (`<hash>.zip`), so a
  // direct key lookup hits. Fall back to matching the destination objectKey so
  // a synthesizer that emits a prefixed / differing objectKey still resolves.
  const asset = fileAssets
    ? (fileAssets.get(code.codeAssetHash) ??
      findFileAssetByObjectKey(fileAssets, code.codeAssetHash))
    : undefined;
  if (!asset) {
    throw new CdkLocalError(
      `AgentCore Runtime '${resolved.logicalId}' code bundle (asset ${code.codeAssetHash}) was not found ` +
        `in the cdk.out asset manifest. ${getEmbedConfig().cliName} invoke-agentcore runs a local from-source ` +
        `build of a fromCodeAsset bundle — re-synthesize the app so the asset is staged in cdk.out and retry. ` +
        `(A fromS3 bundle is downloaded from S3 instead; this runtime has no literal Code.S3.Bucket.)`,
      'LOCAL_INVOKE_AGENTCORE_CODE_ASSET_NOT_FOUND'
    );
  }
  const sourceDir = loader.getAssetSourcePath(cdkOutDir, asset);
  if (!existsSync(sourceDir) || !statSync(sourceDir).isDirectory()) {
    throw new CdkLocalError(
      `AgentCore Runtime '${resolved.logicalId}' code bundle source '${sourceDir}' does not exist or is not a ` +
        `directory. Re-synthesize the app and retry.`,
      'LOCAL_INVOKE_AGENTCORE_CODE_SOURCE_MISSING'
    );
  }
  return buildAgentCoreCodeImage({
    sourceDir,
    runtime: code.runtime,
    entryPoint: code.entryPoint,
    architecture,
    noBuild: options.build === false,
  });
}

/**
 * Build a local image from a fromS3 CodeConfiguration bundle: download +
 * extract the S3 object, run the from-source build over the extracted dir, then
 * clean up the temp dir.
 *
 * Credentials mirror the rest of the command: an `--assume-role` ARN (explicit,
 * or resolved from `--from-cfn-stack` state for the bare form) yields STS temp
 * creds for the download; otherwise `--profile` / the default chain is used.
 * The region is `--region` / `--stack-region` / env / the stack's region.
 *
 * Exported so the issue #570 site-level test can drive the STS failure
 * branch with a mocked STS client -- the same reason
 * `applyAgentCoreCredentialEnv` below is exported. A helper being correct
 * says nothing about whether a given site actually routes through it.
 */
export async function resolveAgentCoreCodeImageFromS3(
  resolved: ResolvedAgentCoreRuntime,
  code: AgentCoreCodeArtifact,
  s3Source: NonNullable<AgentCoreCodeArtifact['s3Source']>,
  options: LocalInvokeAgentCoreOptions,
  architecture: 'x86_64' | 'arm64',
  loaded: LocalStateRecord | undefined,
  stateProvider: LocalStateProvider | undefined
): Promise<string> {
  const logger = getLogger();
  // The bucket should be a literal string by this point — a template-literal
  // bucket falls through from the resolver, an intrinsic bucket was resolved by
  // `resolveFromS3BucketIntrinsic` in the outer command before we got here.
  if (typeof s3Source.bucket !== 'string' || s3Source.bucket.length === 0) {
    throw new CdkLocalError(
      `AgentCore Runtime '${resolved.logicalId}' fromS3 bundle reached the image step with no literal bucket. ` +
        `This is a cdk-local bug — please report it.`,
      'LOCAL_INVOKE_AGENTCORE_FROMS3_BUCKET_UNRESOLVED'
    );
  }
  const location = {
    bucket: s3Source.bucket,
    key: s3Source.key,
    ...(s3Source.versionId !== undefined && { versionId: s3Source.versionId }),
  };
  const region =
    options.region ??
    options.stackRegion ??
    process.env['AWS_REGION'] ??
    process.env['AWS_DEFAULT_REGION'] ??
    resolved.stack.region;

  const assumeRoleArn = await resolveAssumeRoleArn(options, resolved, loaded, stateProvider);
  let credentials:
    | { accessKeyId: string; secretAccessKey: string; sessionToken: string }
    | undefined;
  if (assumeRoleArn) {
    try {
      credentials = await assumeAgentCoreExecutionRole(assumeRoleArn, region, options.profile);
    } catch (err) {
      // Issue #570: not on the issue's list of six, but the same class --
      // see `describeAwsFailureForWarn` in `src/local/credential-error.ts`.
      // `assumeRoleArn` keeps printing for the reason recorded at the
      // `applyAgentCoreCredentialEnv` site below.
      // Same shape and the same reason as the `--sigv4` site above.
      const bundleDetail = assumeRoleDetail(err, 'STS AssumeRole (fromS3 bundle download)');
      logger.warn(
        `--assume-role: STS AssumeRole(${flattenToOneLine(assumeRoleArn)}) failed for the fromS3 bundle download: ` +
          `${bundleDetail}. ` +
          `Falling back to ${options.profile ? `--profile ${options.profile}` : 'the default credentials'}.`
      );
    }
  }

  const bundle = await downloadAndExtractS3Bundle(location, {
    ...(region !== undefined && { region }),
    ...(options.profile !== undefined && { profile: options.profile }),
    ...(credentials !== undefined && { credentials }),
  });
  try {
    return await buildAgentCoreCodeImage({
      sourceDir: bundle.dir,
      runtime: code.runtime,
      entryPoint: code.entryPoint,
      architecture,
      noBuild: options.build === false,
    });
  } finally {
    await bundle.cleanup();
  }
}

/**
 * Find the file asset whose destination objectKey is `<hash>.zip` (matching the
 * `Code.S3.Prefix`'s hash) when the source-hash-keyed lookup misses — covers a
 * synthesizer whose source hash differs from the destination objectKey.
 */
function findFileAssetByObjectKey(
  fileAssets: Map<string, FileAsset>,
  hash: string
): FileAsset | undefined {
  const zip = `${hash}.zip`;
  for (const asset of fileAssets.values()) {
    const hit = Object.values(asset.destinations).some(
      (d) => d.objectKey === zip || d.objectKey.endsWith(`/${zip}`)
    );
    if (hit) return asset;
  }
  return undefined;
}

/**
 * Build the container env + the set of env keys to keep off the `docker run`
 * argv. Substitutes `--from-cfn-stack` state into the template env (reusing the
 * shared state load + image-resolution context — Ref / Fn::Sub / Fn::Join +
 * SSM parameters, with decrypted SecureString values flagged sensitive),
 * applies `--env-vars` overrides, then injects AWS credentials (`--assume-role`
 * STS temp creds — resolving an intrinsic RoleArn from state for bare
 * `--assume-role` — else `--profile` / dev creds).
 *
 * The state provider + loaded record + image context are built once by the
 * caller and shared here, so this does not re-load state.
 */
export async function buildContainerEnv(
  resolved: ResolvedAgentCoreRuntime,
  options: LocalInvokeAgentCoreOptions,
  profileCredentials:
    | { accessKeyId: string; secretAccessKey: string; sessionToken?: string }
    | undefined,
  profileCredsFile: ProfileCredentialsFile | undefined,
  stateProvider: LocalStateProvider | undefined,
  loaded: LocalStateRecord | undefined,
  imageContext: ImageResolutionContext | undefined
): Promise<{ env: Record<string, string>; sensitiveEnvKeys: Set<string> }> {
  const logger = getLogger();
  let templateEnv: Record<string, unknown> = resolved.environmentVariables;
  const sensitiveEnvKeys = new Set<string>();

  if (stateProvider && loaded) {
    const subContext: SubstitutionContext = {
      resources: imageContext?.stateResources ?? loaded.resources,
      consumerRegion: loaded.region,
    };
    const pseudo =
      imageContext?.pseudoParameters ?? derivePseudoParametersFromRegion(loaded.region);
    if (pseudo) subContext.pseudoParameters = pseudo;
    if (imageContext?.stateParameters) subContext.parameters = imageContext.stateParameters;
    if (imageContext?.stateSensitiveParameters?.length) {
      subContext.sensitiveParameters = new Set(imageContext.stateSensitiveParameters);
    }
    const resolver = await stateProvider.buildCrossStackResolver(loaded.region);
    if (resolver) subContext.crossStackResolver = resolver;
    const { env, audit } = await substituteEnvVarsFromStateAsync(templateEnv, subContext);
    templateEnv = env;
    for (const key of audit.resolvedKeys) {
      logger.debug(`${stateProvider.label}: substituted env var ${key}`);
    }
    // Decrypted SecureString SSM values: keep them off the `docker run` argv.
    for (const key of audit.sensitiveKeys) sensitiveEnvKeys.add(key);
    for (const { key, reason } of audit.unresolved) {
      logger.warn(
        `${stateProvider.label}: could not substitute env var ${key} (${reason}). ` +
          `Override it via --env-vars or it will be dropped.`
      );
    }
  }

  const overrides = readEnvOverridesFile(options.envVars);
  const cdkPath = readCdkPathOrUndefined(resolved.resource);
  const envResult = resolveEnvVars(resolved.logicalId, cdkPath, templateEnv, overrides);
  for (const key of envResult.unresolved) {
    const overrideKeyExample = cdkPath?.replace(/\/Resource$/, '') ?? resolved.logicalId;
    logger.warn(
      `Environment variable ${key} contains a CloudFormation intrinsic and was dropped. ` +
        `Override it with --env-vars (e.g. {"${overrideKeyExample}":{"${key}":"<literal>"}}), ` +
        `or pass a state-source flag (e.g. --from-cfn-stack) to recover deployed values.`
    );
  }

  const dockerEnv: Record<string, string> = { ...envResult.resolved };
  const assumeRoleArn = await resolveAssumeRoleArn(options, resolved, loaded, stateProvider);
  await applyAgentCoreCredentialEnv(dockerEnv, {
    ...(assumeRoleArn !== undefined && { assumeRoleArn }),
    ...(options.region !== undefined && { region: options.region }),
    ...(options.profile !== undefined && { profile: options.profile }),
    ...(profileCredentials !== undefined && { profileCredentials }),
    ...(profileCredsFile !== undefined && {
      profileCredsFile: {
        containerPath: profileCredsFile.containerPath,
        profileName: profileCredsFile.profileName,
      },
    }),
  });
  return { env: dockerEnv, sensitiveEnvKeys };
}

/**
 * Resolve a fromS3 bundle's intrinsic `Code.S3.Bucket` to a literal bucket
 * name in place on `resolved.codeArtifact.s3Source.bucket`. Uses the SAME
 * state-substitution machinery env vars use under `--from-cfn-stack`, so
 * every cross-stack intrinsic that path supports (`Ref` / `Fn::ImportValue` /
 * `Fn::GetStackOutput`) is supported transparently here.
 *
 * No-op when there is no intrinsic to resolve. Errors when no state is
 * available, or when the substitution returns a non-string / unresolved value.
 *
 * Exported so a unit test can drive the gate without the full Docker pipeline.
 */
export async function resolveFromS3BucketIntrinsic(
  resolved: ResolvedAgentCoreRuntime,
  stateProvider: LocalStateProvider | undefined,
  loaded: LocalStateRecord | undefined,
  imageContext: ImageResolutionContext | undefined
): Promise<void> {
  const s3Source = resolved.codeArtifact?.s3Source;
  if (!s3Source || s3Source.bucketIntrinsic === undefined) return;
  if (s3Source.bucket !== undefined) return; // already resolved

  if (!stateProvider || !loaded) {
    throw new CdkLocalError(
      `AgentCore Runtime '${resolved.logicalId}' fromS3 bundle's Code.S3.Bucket is an unresolved intrinsic ` +
        `(${describeIntrinsic(s3Source.bucketIntrinsic)}). ` +
        `Pass --from-cfn-stack so its physical bucket name can be resolved against the deployed stack state.`,
      'LOCAL_INVOKE_AGENTCORE_FROMS3_BUCKET_INTRINSIC_NO_STATE'
    );
  }

  const subContext: SubstitutionContext = {
    resources: imageContext?.stateResources ?? loaded.resources,
    consumerRegion: loaded.region,
  };
  const pseudo = imageContext?.pseudoParameters ?? derivePseudoParametersFromRegion(loaded.region);
  if (pseudo) subContext.pseudoParameters = pseudo;
  const crossStackResolver = await stateProvider.buildCrossStackResolver(loaded.region);
  if (crossStackResolver) subContext.crossStackResolver = crossStackResolver;

  const result = await substituteAgainstStateAsync(s3Source.bucketIntrinsic, subContext);
  if (result.kind !== 'literal') {
    throw new CdkLocalError(
      `Could not resolve AgentCore Runtime '${resolved.logicalId}' fromS3 Code.S3.Bucket intrinsic ` +
        `(${describeIntrinsic(s3Source.bucketIntrinsic)}) against the --from-cfn-stack state: ${result.reason}. ` +
        `Confirm the referenced resource / export exists in the deployed stack.`,
      'LOCAL_INVOKE_AGENTCORE_FROMS3_BUCKET_INTRINSIC_UNRESOLVED'
    );
  }
  if (typeof result.value !== 'string' || result.value.length === 0) {
    throw new CdkLocalError(
      `AgentCore Runtime '${resolved.logicalId}' fromS3 Code.S3.Bucket intrinsic resolved to a ` +
        `${typeof result.value} value, not a bucket name string. ` +
        `(${describeIntrinsic(s3Source.bucketIntrinsic)})`,
      'LOCAL_INVOKE_AGENTCORE_FROMS3_BUCKET_INTRINSIC_NOT_STRING'
    );
  }
  s3Source.bucket = result.value;
  getLogger().info(
    `Resolved fromS3 Code.S3.Bucket from state: ${describeIntrinsic(s3Source.bucketIntrinsic)} -> ${result.value}`
  );
}

/** Render the intrinsic key for an error / log message (e.g. `Ref:Bucket1`). */
function describeIntrinsic(value: unknown): string {
  if (!value || typeof value !== 'object') return String(value);
  const obj = value as Record<string, unknown>;
  const key = Object.keys(obj)[0] ?? '?';
  const arg = obj[key];
  if (typeof arg === 'string') return `${key}:${arg}`;
  return key;
}

/**
 * Build the `--from-cfn-stack` image-resolution context + return the loaded
 * state record (loaded once, reused by env substitution + role resolution).
 * Mirrors `run-task`'s `buildEcsImageResolutionContext`: pseudo parameters
 * (region + STS account id), the deployed resources, and SSM template
 * parameters (decrypted SecureString logical ids flagged sensitive).
 */
export async function buildAgentCoreImageContext(
  candidate: StackInfo,
  stateProvider: LocalStateProvider,
  options: LocalInvokeAgentCoreOptions
): Promise<{ context: ImageResolutionContext | undefined; loaded: LocalStateRecord | undefined }> {
  const logger = getLogger();
  const region =
    options.region ??
    process.env['AWS_REGION'] ??
    process.env['AWS_DEFAULT_REGION'] ??
    candidate.region;

  let accountId: string | undefined;
  try {
    accountId = await resolveCallerAccountId(region, options.profile);
  } catch (err) {
    // Issue #570: never interpolate the SDK error's `message` into this
    // default-level line -- see `describeAwsFailureForWarn` in
    // `src/local/credential-error.ts` for which half of it prints here and
    // which is withheld to `debug`.
    logger.warn(
      `--from-cfn-stack: STS GetCallerIdentity failed: ${describeAwsFailureForWarn(err, 'STS GetCallerIdentity')}. ` +
        'A same-stack ECR image URI referencing ${AWS::AccountId} may not resolve.'
    );
  }

  const context: ImageResolutionContext = {};
  const pseudo = derivePseudoParametersFromRegion(region, accountId);
  if (pseudo) context.pseudoParameters = pseudo;

  const loaded = await stateProvider.load(candidate.stackName, candidate.region);
  if (loaded) {
    context.stateResources = loaded.resources;
    if (stateProvider.resolveTemplateSsmParameters) {
      const ssm = await stateProvider.resolveTemplateSsmParameters(candidate.template);
      if (Object.keys(ssm.values).length > 0) context.stateParameters = ssm.values;
      if (ssm.secureStringLogicalIds.length > 0) {
        context.stateSensitiveParameters = ssm.secureStringLogicalIds;
      }
    }
  } else {
    // load() returned undefined — capture the provider's failure
    // detail so the resolver's "needs deployed state" error reports
    // what AWS actually said instead of telling the user to re-pass
    // a flag they already passed.
    const loadError = stateProvider.getLastLoadError?.();
    if (loadError) context.stateLoadFailureMessage = loadError;
  }
  return { context, loaded: loaded ?? undefined };
}

/** STS `GetCallerIdentity` for the `${AWS::AccountId}` pseudo parameter (threads `--profile`). */
async function resolveCallerAccountId(
  region: string | undefined,
  profile: string | undefined
): Promise<string | undefined> {
  const { STSClient, GetCallerIdentityCommand } = await import('@aws-sdk/client-sts');
  const sts = new STSClient(buildStsClientConfig({ region, profile }));
  try {
    const identity = await sts.send(new GetCallerIdentityCommand({}));
    return identity.Account;
  } finally {
    sts.destroy();
  }
}

/**
 * Inject AWS credentials into the container env. Precedence:
 *   1. `--assume-role` → STS-issued temp creds for the resolved ARN (on
 *      STS failure, warn + fall through to dev creds).
 *   2. dev shell creds (`forwardAwsEnv`) + `--profile` overlay
 *      ({@link applyProfileCredentialsOverlay}) + the bind-mounted
 *      credentials-file env so handler `fromIni({ profile })` resolves.
 *
 * Exported so a unit test can lock the binding (mock STS) without driving
 * the full synth + docker pipeline.
 */
export async function applyAgentCoreCredentialEnv(
  dockerEnv: Record<string, string>,
  args: {
    assumeRoleArn?: string;
    region?: string;
    profile?: string;
    profileCredentials?: { accessKeyId: string; secretAccessKey: string; sessionToken?: string };
    profileCredsFile?: { containerPath: string; profileName: string };
  }
): Promise<void> {
  const logger = getLogger();
  let assumeSucceeded = false;
  if (args.assumeRoleArn) {
    const stsRegion = args.region ?? process.env['AWS_REGION'] ?? process.env['AWS_DEFAULT_REGION'];
    try {
      const creds = await assumeAgentCoreExecutionRole(args.assumeRoleArn, stsRegion, args.profile);
      dockerEnv['AWS_ACCESS_KEY_ID'] = creds.accessKeyId;
      dockerEnv['AWS_SECRET_ACCESS_KEY'] = creds.secretAccessKey;
      dockerEnv['AWS_SESSION_TOKEN'] = creds.sessionToken;
      if (stsRegion) dockerEnv['AWS_REGION'] = stsRegion;
      assumeSucceeded = true;
    } catch (err) {
      // Issue #570: never interpolate the SDK error's `message` into this
      // default-level line -- see `describeAwsFailureForWarn` in
      // `src/local/credential-error.ts` for which half of it prints here and
      // which is withheld to `debug`.
      //
      // `args.assumeRoleArn` KEEPS printing. It is the developer's own
      // `--assume-role` value or an ARN read from their own deployed stack
      // state, and an ARN is a public identifier -- the same call issue #555
      // made for the access-key-id it left quoted. It is also load-bearing
      // for `tests/integration/local-studio/verify.sh`, which proves
      // `--assume-role` threaded from the CLI to the spawned child by
      // finding the ARN in the studio log ring.
      //
      // It is FLATTENED all the same: the bare `--assume-role` form resolves
      // the ARN from a live `GetAgentRuntime` response, so it is wire-derived
      // text on the line the helper beside it just made forge-proof. On a real
      // ARN the call is a no-op, so the integ grep is unaffected.
      // Same shape and the same reason as the two sites above.
      const assumeDetail = assumeRoleDetail(err, 'STS AssumeRole');
      logger.warn(
        `--assume-role: STS AssumeRole(${flattenToOneLine(args.assumeRoleArn)}) failed: ${assumeDetail}. ` +
          "Falling back to the developer's shell credentials."
      );
    }
  }
  if (!assumeSucceeded) {
    forwardAwsEnv(dockerEnv);
    applyProfileCredentialsOverlay(dockerEnv, args.profileCredentials, false);
    if (args.profileCredsFile) {
      dockerEnv['AWS_SHARED_CREDENTIALS_FILE'] = args.profileCredsFile.containerPath;
      dockerEnv['AWS_PROFILE'] = args.profileCredsFile.profileName;
    }
  }
}

/**
 * Resolve the role ARN to assume, honoring the three `--assume-role` forms.
 * Bare `--assume-role` uses the runtime's literal `RoleArn`; when that is an
 * intrinsic (the common L2 case — `Fn::GetAtt` to an auto-created role) it
 * resolves the execution-role ARN from `--from-cfn-stack` state first; when
 * that misses (issue #187 — `ListStackResources` returns the role's name,
 * not its ARN, so `attributes.Arn` is empty on the CFn state map) it falls
 * back to `stateProvider.resolveAgentCoreRuntimeRoleArn(<physicalId>)`
 * (a `bedrock-agentcore-control:GetAgentRuntime` call) so a same-stack
 * execution role still resolves. Only warns + falls back to dev creds
 * when all of those miss.
 */
export async function resolveAssumeRoleArn(
  options: LocalInvokeAgentCoreOptions,
  resolved: ResolvedAgentCoreRuntime,
  loaded: LocalStateRecord | undefined,
  stateProvider?: Pick<LocalStateProvider, 'resolveAgentCoreRuntimeRoleArn'>
): Promise<string | undefined> {
  if (typeof options.assumeRole === 'string') {
    // Issue #607: `cdkl start-api` validates `--assume-role <arn>` at PARSE
    // time (`parseAssumeRoleToken`, wired as that command's `argParser`), and
    // this command has no argParser at all — so the identical spelling reached
    // STS unchecked here. Rejecting at this resolution point restores the
    // symmetry AND keeps an unbounded value off the callers' warn lines, which
    // interpolate `flattenToOneLine(assumeRoleArn)` with no length cap of
    // their own. A hard error, matching `parseAssumeRoleToken`: the user typed
    // this, so silently ignoring it and falling back to shell credentials
    // would be the wrong shape.
    if (!isIamRoleArn(options.assumeRole)) {
      throw new CdkLocalError(
        `Invalid --assume-role value: expected an IAM role ARN like ` +
          `arn:aws:iam::123456789012:role/MyRole, got ${describeRejectedRoleArn(options.assumeRole)}.`,
        'LOCAL_INVOKE_AGENTCORE_ASSUME_ROLE_INVALID'
      );
    }
    return options.assumeRole;
  }
  if (options.assumeRole !== true) return undefined;
  // Bare --assume-role from here on.
  // Issue #607: the template's literal `RoleArn`, the agentcore twin of
  // `local-start-api`'s `Properties.Role`. Shape-checked, then FALL THROUGH to
  // the state lookup on rejection — a runtime whose template RoleArn is not a
  // usable ARN was always meant to be recoverable from deployed state.
  if (resolved.roleArn !== undefined) {
    if (isIamRoleArn(resolved.roleArn)) return resolved.roleArn;
    getLogger().warn(
      `--assume-role: the template RoleArn for '${resolved.logicalId}' is not a well-formed IAM role ARN: ${describeRejectedRoleArn(resolved.roleArn)}. Ignoring it.`
    );
  }
  if (loaded) {
    const fromState = resolveExecutionRoleArnFromState(loaded, resolved.logicalId, 'RoleArn');
    if (fromState) {
      getLogger().debug(
        `--assume-role: resolved RoleArn from state: ${flattenToOneLine(fromState)}`
      );
      return fromState;
    }
    // Issue #187 fallback: state-only lookup misses for sibling-stack
    // exec roles because `ListStackResources` returns the role's name,
    // not its ARN. The runtime's deploy-time `roleArn` carries the
    // full ARN — read it via `bedrock-agentcore-control:GetAgentRuntime`.
    const runtimePhysicalId = loaded.resources[resolved.logicalId]?.physicalId;
    if (stateProvider?.resolveAgentCoreRuntimeRoleArn && runtimePhysicalId) {
      const liveArn = await stateProvider.resolveAgentCoreRuntimeRoleArn(runtimePhysicalId);
      // Issue #607: shape-checked here as well as inside cdk-local's own
      // provider, because `stateProvider` is an INTERFACE a host CLI may
      // implement. Same reasoning as the `local-invoke.ts` sibling.
      if (liveArn !== undefined && !isIamRoleArn(liveArn)) {
        getLogger().warn(
          `--assume-role: GetAgentRuntime for '${resolved.logicalId}' produced a value that is not a well-formed IAM role ARN: ${describeRejectedRoleArn(liveArn)}. Ignoring it.`
        );
      } else if (liveArn) {
        // Issue #570: FLATTENED. `liveArn` is the deployed `roleArn` off a
        // `GetAgentRuntime` response with only a `startsWith('arn:')` check, and
        // `info` is a default level studio mirrors into an HTTP-served ring.
        // This is the SUCCESS path, so it is more reachable than the warn.
        getLogger().info(
          `--assume-role: auto-resolved execution role from GetAgentRuntime: ${flattenToOneLine(liveArn)}`
        );
        return liveArn;
      }
    }
  }
  getLogger().warn(
    "--assume-role passed without an ARN, but the runtime's RoleArn is not a literal ARN in the template " +
      (loaded
        ? 'and could not be resolved from the deployed stack state. '
        : 'and no --from-cfn-stack state is available to resolve it. ') +
      'Pass the ARN explicitly: --assume-role <arn>. ' +
      "Falling back to the developer's shell credentials."
  );
  return undefined;
}

export function emitResult(result: AgentCoreInvokeResult): void {
  const logger = getLogger();
  if (result.status >= 400) {
    logger.warn(`Agent /invocations returned HTTP ${result.status}.`);
    process.exitCode = 1;
  }
  if (result.streamed) {
    // The SSE body was already written chunk-by-chunk via the onChunk sink;
    // just terminate with a newline so the shell prompt resumes cleanly.
    process.stdout.write('\n');
    return;
  }
  process.stdout.write(`${result.raw}\n`);
}

/**
 * Finish a `/ws` exchange: the frames were already streamed to stdout via the
 * onMessage sink, so just terminate with a newline (so the shell prompt resumes
 * cleanly) and note the frame count at debug level.
 */
export function emitWsResult(result: AgentCoreWsResult): void {
  process.stdout.write('\n');
  getLogger().debug(`Agent /ws closed after ${result.frames} frame(s).`);
}

/**
 * Build the JSON-RPC request to send to an MCP runtime from `--event`:
 *   - no `--event` (empty object) → `tools/list` (discover the server's tools),
 *   - an object with a string `method` → that method + its `params`,
 *   - anything else → a fail-fast error.
 *
 * Exported for unit testing.
 */
export function buildMcpRequest(event: unknown): McpJsonRpcRequest {
  if (event === undefined || event === null) return { method: 'tools/list', params: {} };
  if (typeof event !== 'object' || Array.isArray(event)) {
    throw new CdkLocalError(
      'MCP --event must be a JSON object describing a JSON-RPC request ' +
        '(e.g. {"method":"tools/call","params":{"name":"...","arguments":{...}}}).',
      'LOCAL_INVOKE_AGENTCORE_MCP_EVENT_INVALID'
    );
  }
  const obj = event as Record<string, unknown>;
  if (Object.keys(obj).length === 0) return { method: 'tools/list', params: {} };
  if (typeof obj['method'] !== 'string') {
    throw new CdkLocalError(
      'MCP --event must include a string "method" (a JSON-RPC method such as ' +
        `"tools/list" or "tools/call"). Got keys: ${Object.keys(obj).join(', ')}.`,
      'LOCAL_INVOKE_AGENTCORE_MCP_EVENT_INVALID'
    );
  }
  return {
    method: obj['method'],
    ...(obj['params'] !== undefined && { params: obj['params'] }),
  };
}

/** Print the MCP JSON-RPC response; exit 1 when it carried a JSON-RPC error. */
export function emitMcpResult(result: McpInvokeResult): void {
  if (!result.ok) {
    getLogger().warn('MCP server returned a JSON-RPC error.');
    process.exitCode = 1;
  }
  process.stdout.write(`${result.raw}\n`);
}

/**
 * Build the JSON-RPC request to send to an A2A runtime from `--event`:
 *   - no `--event` (empty object) → `agent/getCard` (discover the agent's card),
 *   - an object with a string `method` → that method + its `params`,
 *   - anything else → a fail-fast error.
 *
 * Exported for unit testing.
 */
export function buildA2aRequest(event: unknown): A2aJsonRpcRequest {
  if (event === undefined || event === null) return { method: 'agent/getCard', params: {} };
  if (typeof event !== 'object' || Array.isArray(event)) {
    throw new CdkLocalError(
      'A2A --event must be a JSON object describing a JSON-RPC request ' +
        '(e.g. {"method":"tasks/send","params":{"id":"...","message":{...}}}).',
      'LOCAL_INVOKE_AGENTCORE_A2A_EVENT_INVALID'
    );
  }
  const obj = event as Record<string, unknown>;
  if (Object.keys(obj).length === 0) return { method: 'agent/getCard', params: {} };
  if (typeof obj['method'] !== 'string') {
    throw new CdkLocalError(
      'A2A --event must include a string "method" (a JSON-RPC method such as ' +
        `"agent/getCard" or "tasks/send"). Got keys: ${Object.keys(obj).join(', ')}.`,
      'LOCAL_INVOKE_AGENTCORE_A2A_EVENT_INVALID'
    );
  }
  return {
    method: obj['method'],
    ...(obj['params'] !== undefined && { params: obj['params'] }),
  };
}

/** Print the A2A JSON-RPC response; exit 1 when it carried a JSON-RPC error. */
export function emitA2aResult(result: A2aInvokeResult): void {
  if (!result.ok) {
    getLogger().warn('A2A server returned a JSON-RPC error.');
    process.exitCode = 1;
  }
  process.stdout.write(`${result.raw}\n`);
}

/** Map a `--platform` value to the architecture `buildContainerImage` expects. */
export function platformToArchitecture(platform: string): 'x86_64' | 'arm64' {
  return platform === 'linux/amd64' ? 'x86_64' : 'arm64';
}

function forwardAwsEnv(env: Record<string, string>): void {
  const passThrough = [
    'AWS_ACCESS_KEY_ID',
    'AWS_SECRET_ACCESS_KEY',
    'AWS_SESSION_TOKEN',
    'AWS_REGION',
    'AWS_DEFAULT_REGION',
  ] as const;
  for (const key of passThrough) {
    const value = process.env[key];
    if (value !== undefined) env[key] = value;
  }
}

async function assumeAgentCoreExecutionRole(
  roleArn: string,
  region: string | undefined,
  profile: string | undefined
): Promise<{ accessKeyId: string; secretAccessKey: string; sessionToken: string }> {
  // GUARDED SEND (issue #607). The THIRD of the five `new AssumeRoleCommand(`
  // sites in `src/`, and the one that matters most after the two in
  // `utils/role-arn.ts`: every one of this function's three callers passes
  // `resolveAssumeRoleArn(...)`'s output, so the value can be the runtime's
  // template `RoleArn`, a deployed-state read, a `GetAgentRuntime` response,
  // or a host-supplied `LocalStateProvider`'s return. Those are all checked at
  // their resolution points — but `--assume-role <arn>` returns EARLY from
  // `resolveAssumeRoleArn` before any of them, and unlike `start-api` this
  // command has no `parseAssumeRoleToken` argParser, so that spelling reached
  // STS unchecked while its `start-api` equivalent was rejected at parse.
  //
  // Shares `refusedRoleArnMessage` with the two guarded sends in
  // `utils/role-arn.ts` so all three word the refusal identically. Refuses
  // BEFORE the SDK is even imported, so nothing is loaded, built or logged.
  //
  // Throws `AssumeRoleFailure`, NOT a bare error: all three callers render
  // through `assumeRoleDetail`, which falls back to
  // `describeAwsFailureForWarn` unless the error is an
  // `AssumeRoleFailure`, and a non-service error is that policy's WITHHELD
  // branch — so a `CdkLocalError` here would come out as
  // `CdkLocalError; 213-character message withheld`, the exact double-withhold
  // `AssumeRoleFailure`'s own JSDoc exists to document.
  if (!isIamRoleArn(roleArn)) {
    throw new AssumeRoleFailure(
      refusedRoleArnMessage(roleArn),
      `the role ARN is not well-formed: ${describeRejectedRoleArn(roleArn)}`
    );
  }
  const { STSClient, AssumeRoleCommand } = await import('@aws-sdk/client-sts');
  // Thread `--profile` so AssumeRole is signed with the profile's
  // credentials, not the default env-shadowed chain (issue #245).
  const sts = new STSClient(buildStsClientConfig({ region, profile }));
  try {
    const response = await sts.send(
      new AssumeRoleCommand({
        RoleArn: roleArn,
        RoleSessionName: `${getEmbedConfig().resourceNamePrefix}-invoke-agentcore-${Date.now()}`,
        DurationSeconds: 3600,
      })
    );
    const creds = response.Credentials;
    if (!creds?.AccessKeyId || !creds.SecretAccessKey || !creds.SessionToken) {
      // Issue #579 review round 5 — identifiable, for the same reason the
      // twin in `utils/role-arn.ts` is: all THREE call sites of this helper
      // re-render what it throws through `describeAwsFailureForWarn`, and a
      // bare `Error` has no `$fault`, so cdk-local's own sentence was withheld
      // to `Error; N-character message withheld` at every one of them.
      // `message` is byte-identical to what it threw before; only the class
      // and the added `detail` are new.
      throw new AssumeRoleFailure(
        `AssumeRole(${flattenToOneLine(roleArn)}) returned no usable credentials.`,
        'the response carried no usable credentials'
      );
    }
    return {
      accessKeyId: creds.AccessKeyId,
      secretAccessKey: creds.SecretAccessKey,
      sessionToken: creds.SessionToken,
    };
  } finally {
    sts.destroy();
  }
}

export async function readEvent(options: LocalInvokeAgentCoreOptions): Promise<unknown> {
  if (options.event && options.eventStdin) {
    throw new Error('--event and --event-stdin are mutually exclusive.');
  }
  if (options.eventStdin) {
    return parseEvent(await readStdin(), '<stdin>');
  }
  if (options.event) {
    let raw: string;
    try {
      raw = readFileSync(options.event, 'utf-8');
    } catch (err) {
      throw new Error(
        `Failed to read --event file '${options.event}': ${err instanceof Error ? err.message : String(err)}`
      );
    }
    return parseEvent(raw, options.event);
  }
  return {};
}

function parseEvent(raw: string, source: string): unknown {
  try {
    return JSON.parse(raw);
  } catch (err) {
    throw new Error(
      `Failed to parse event payload from ${source} as JSON: ${err instanceof Error ? err.message : String(err)}`
    );
  }
}

async function readStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin as AsyncIterable<Buffer | string>) {
    chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : chunk);
  }
  return Buffer.concat(chunks).toString('utf-8');
}

/**
 * Read `process.stdin` line-buffered and yield each line as a string (trailing
 * `\r?\n` stripped). The async iterable completes when stdin EOFs (Ctrl-D /
 * end-of-pipe), and surrenders the underlying stream when its `return()` is
 * called — so the WS client can close down the source when the server closes
 * first without leaving stdin held open.
 *
 * Exported so a unit test can drive the iterable shape directly.
 */
export async function* readStdinLines(): AsyncIterable<string> {
  const { createInterface } = await import('node:readline');
  const rl = createInterface({ input: process.stdin, crlfDelay: Infinity });
  try {
    for await (const line of rl) {
      // Issue #276 — skip strictly-empty lines in interactive mode.
      // Without this, pressing Enter without typing sends an empty WS text
      // frame; a loop-mode agent (e.g. the integ fixture's EchoAgent) then
      // replies with a half-line like `loop-echo:` that visually corrupts
      // the REPL. A whitespace-only line is NOT skipped — sending spaces
      // can be a deliberate signal for some agents.
      if (line.length === 0) continue;
      yield line;
    }
  } finally {
    rl.close();
  }
}

/**
 * Issue #276 — REPL prompt indicator for the auto-detected TTY mode.
 * Written to stdout (no trailing newline) after each received agent frame,
 * so the user has a clear visual signal that the REPL is waiting for input.
 */
export const WS_REPL_PROMPT = '> ';

/**
 * Issue #276 — wrap `onMessage` so each agent WS text frame lands on its
 * own line when the auto-REPL is active AND a `>` prompt indicator
 * follows. WS frames carry no trailing `\n` by protocol (each frame is a
 * discrete message); the cloud `/ws` endpoint behaves the same. That's
 * correct on the wire but visually run-on in an interactive terminal —
 * the user's next keystroke concatenates onto the last frame's tail char
 * and they have no signal that the REPL is waiting for input. In
 * interactive mode we append `\n` (unless already present) and then write
 * a `>` prompt, so each agent message is presented as its own line and
 * the next input prompt is visible.
 *
 * Non-interactive (piped / CI) is unchanged — the raw stdout shape is
 * what scripts rely on, and the WS-protocol-faithful pass-through stays.
 */
export function wrapWsOnMessage(
  sink: (text: string) => void,
  interactive: boolean
): (text: string) => void {
  if (!interactive) return sink;
  return (text) => {
    sink(text.endsWith('\n') ? text : `${text}\n`);
    sink(WS_REPL_PROMPT);
  };
}

export function readEnvOverridesFile(filePath: string | undefined): EnvOverrideFile | undefined {
  if (!filePath) return undefined;
  let raw: string;
  try {
    raw = readFileSync(filePath, 'utf-8');
  } catch (err) {
    throw new Error(
      `Failed to read --env-vars file '${filePath}': ${err instanceof Error ? err.message : String(err)}`
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(
      `Failed to parse --env-vars file '${filePath}' as JSON: ${err instanceof Error ? err.message : String(err)}`
    );
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`--env-vars file '${filePath}' must contain a JSON object at the top level.`);
  }
  return parsed as EnvOverrideFile;
}

/**
 * Issue #255 — `cdkl invoke-agentcore --watch` main loop. Wraps the
 * `/ws` dispatch in a reload-driven loop:
 *
 *   1. Open the WebSocket against the current container; stream frames
 *      to stdout. The connection is bound to an abort signal the
 *      watcher fires when a source change is detected.
 *   2. When the agent closes the stream naturally (no reload pending),
 *      the loop exits — `--watch` does NOT auto-reconnect on a benign
 *      close (the user can `^C` to leave or just re-run the command).
 *   3. When a reload fires, the abort signal closes the WS cleanly
 *      (resolves with the current frame count, not a reject), then the
 *      classifier picks `'rebuild'` vs `'soft-reload'` and the
 *      corresponding container-swap helper runs. After the swap, the
 *      loop re-opens the WS against the new / restarted container with
 *      the same `--event` first frame + a fresh stdin reader (when
 *      `interactive` mode is active — see `args.interactive`).
 *
 * Active `/ws` socket handling: the AgentCore `/ws` protocol does not
 * define mid-session container handoff, so the only honest local-dev
 * semantic is "close the socket cleanly and let the client reconnect."
 * The reload firing logs that loudly so the user sees it.
 *
 * `softReload` and `rebuild` are passed as callbacks so the watch loop
 * itself stays uncoupled from the boot pipeline + the docker helpers
 * the outer command owns. The host-side teardown (state provider
 * dispose, container removal, profile creds-file dispose) is the
 * outer command's `finally` cleanup — the watch loop only swaps the
 * one agent container.
 *
 * @internal — exported so a unit test can drive the loop without the
 * full Docker + chokidar pipeline.
 */
export async function runAgentCoreWatchLoop(args: {
  containerHost: string;
  hostPort: number;
  event: unknown;
  sessionId: string;
  timeoutMs: number;
  /**
   * When `true`, attach `readStdinLines()` as the WS frame source so each
   * stdin line becomes a follow-up text frame (multi-turn REPL). The caller
   * computes this from `process.stdin.isTTY === true`; passing it in keeps
   * `runAgentCoreWatchLoop` testable without mocking the TTY.
   */
  interactive: boolean;
  authorization?: string;
  options: LocalInvokeAgentCoreOptions;
  /** Original CDK target string (display path / logical id) so the resolver can re-pick the stack on reload. */
  resolvedTarget: string;
  resolved: ResolvedAgentCoreRuntime;
  synthesizer: Synthesizer;
  synthOpts: SynthesisOptions;
  stacks: StackInfo[];
  /**
   * Rebuild the agent container from scratch. SIGTERM + `docker rm -f`
   * the old container, re-resolve the image (re-running the same
   * `cdk synth` + image-build pipeline the cold start runs), then
   * `docker run` a fresh container. Returns the new (containerId,
   * hostPort, stacks) so the loop can update its local state.
   */
  rebuild: () => Promise<{ containerId: string; hostPort: number; stacks: StackInfo[] }>;
  /**
   * Soft-reload the agent container in place. `docker cp` the
   * freshly-synthed asset directory contents into the existing
   * container's WORKDIR + `docker restart` it. No `docker build`, no
   * container swap — the container ID + host port are preserved.
   * Returns the freshly-synthed stacks for the next classifier firing's
   * asset-context lookup.
   */
  softReload: (newAssetSourceDir: string) => Promise<{ stacks: StackInfo[] }>;
  /** Test hook for the WS dispatch (replaces `invokeAgentCoreWs`). */
  __wsInvoker?: typeof invokeAgentCoreWs;
  /**
   * Test hook for the watcher factory. The default uses
   * {@link createFileWatcher} against the cwd, honoring `cdk.json`'s
   * `watch.include` / `watch.exclude` like every other `--watch` path.
   */
  __watcherFactory?: (onChange: (changedPaths: readonly string[]) => void) => FileWatcher;
  /** Test hook for the ping wait between container boot + WS open. */
  __waitForPing?: (host: string, port: number) => Promise<void>;
  /**
   * Test hook for the per-firing classifier context builder. The default
   * runs a fresh `synthesizer.synthesize(synthOpts)` + the
   * {@link loadAgentCoreAssetContext} + {@link deriveOldAssetHash}
   * pipeline; the override lets a unit test drive the rebuild vs
   * soft-reload branches directly without standing up a real
   * AssetManifestLoader.
   */
  __classifierContext?: (
    changedPaths: readonly string[]
  ) => Promise<ReloadAssetContext | undefined>;
}): Promise<void> {
  const logger = getLogger();
  const wsInvoker = args.__wsInvoker ?? invokeAgentCoreWs;
  const waitForPing = args.__waitForPing ?? waitForAgentCorePing;

  let currentHostPort = args.hostPort;
  let currentStacks = args.stacks;

  // Reload queue: serialize reload events so two rapid edits don't run
  // two rebuilds in parallel. The chain `then`s every new event onto
  // the previous one, just like start-api + the ECS service emulator.
  let reloadChain: Promise<unknown> = Promise.resolve();
  // Per-iteration AbortController that the watcher's onChange fires
  // to close the in-flight WS so the reload can swap the container.
  // Recreated each iteration so a fresh WS gets a fresh signal.
  let currentAbort: AbortController | undefined;
  // Flips to true when a reload was triggered for the CURRENT WS
  // session — used to decide whether the post-WS path enters another
  // iteration (reload) or exits the loop (agent closed without a
  // pending reload).
  let pendingReload = false;
  // Flips to true if a rebuild or soft-reload callback throws — both
  // paths teardown the old container before bringing the replacement
  // up, so `currentHostPort` may point at a torn-down container. The
  // main loop checks this BEFORE the next waitForPing so we bail out
  // instead of blocking for the ~30s ping timeout.
  let reloadFailed = false;
  // Tracks "the watcher fired during a reload"; the watch loop's
  // serialized reloadChain handles re-firing without re-entry races.

  const watcherFactory =
    args.__watcherFactory ??
    ((onChange) => {
      const watchRoot = process.cwd();
      const { ignored, shouldTrigger, excludePatterns } = createWatchPredicates({
        watchRoot,
        output: args.options.output,
        watchConfig: resolveWatchConfig(),
      });
      logger.info(
        `Watching ${watchRoot} for source changes (excluding ${excludePatterns.join(', ')}).`
      );
      return createFileWatcher({ paths: [watchRoot], ignored, shouldTrigger, onChange });
    });

  const cdkOutDir = args.options.output;
  const assetLoader = new AssetManifestLoader();

  const watcher = watcherFactory((changedPaths) => {
    const next = reloadChain.then(async () => {
      // Classify per firing. The classifier needs the OLD + NEW asset
      // hashes + the new asset source directory; we re-derive against
      // the freshly-synthed stacks before deciding rebuild vs
      // soft-reload. A classifier-context build failure (e.g. asset
      // manifest missing) falls back to rebuild — the same conservative
      // default the ECS emulator's reload pathway uses.
      let verdict: ReloadVerdict = { kind: 'rebuild', reason: 'classifier not consulted' };
      let assetCtx: ReloadAssetContext | undefined;
      try {
        if (args.__classifierContext) {
          assetCtx = await args.__classifierContext(changedPaths);
        } else {
          const { stacks: freshStacks } = await args.synthesizer.synthesize(args.synthOpts);
          const oldAssetHash = await deriveOldAssetHash({
            resolvedTarget: args.resolvedTarget,
            resolved: args.resolved,
            stacks: currentStacks,
            cdkOutDir,
            assetLoader,
          });
          assetCtx = await loadAgentCoreAssetContext({
            resolvedTarget: args.resolvedTarget,
            resolved: args.resolved,
            stacks: freshStacks,
            cdkOutDir,
            assetLoader,
            oldAssetHash,
          });
        }
        verdict = classifySourceChange(changedPaths, assetCtx);
        logger.info(
          `Detected source change (${changedPaths.length} path(s)); verdict=${verdict.kind} (${verdict.reason}).`
        );
      } catch (err) {
        logger.warn(
          `Reload: classifier context unavailable ` +
            `(${err instanceof Error ? err.message : String(err)}); falling back to rebuild.`
        );
        verdict = {
          kind: 'rebuild',
          reason: 'classifier context unavailable; falling back to rebuild',
        };
      }
      // Mark the pending reload BEFORE aborting so the WS dispatch
      // promise (which resolves on abort) knows to re-enter the loop
      // instead of exiting.
      pendingReload = true;
      logger.warn(
        'cdkl invoke-agentcore --watch: source change detected; closing the active /ws ' +
          'socket so the client can reconnect to the rebuilt container.'
      );
      // Abort the current WS exchange so the dispatch promise resolves
      // and the loop iteration can proceed to the swap step.
      currentAbort?.abort();
      try {
        if (verdict.kind === 'soft-reload') {
          const { stacks: newStacks } = await args.softReload(verdict.newAssetSourceDir);
          currentStacks = newStacks;
          logger.info(
            `Reload: soft-reloaded the agent container (docker cp + docker restart; ` +
              `container ID + host port preserved).`
          );
        } else {
          const {
            containerId: _newId,
            hostPort: newHostPort,
            stacks: newStacks,
          } = await args.rebuild();
          currentHostPort = newHostPort;
          currentStacks = newStacks;
          logger.info(`Reload: rebuilt the agent container.`);
        }
      } catch (err) {
        // Both rebuild and soft-reload teardown the running container
        // before bringing the replacement up. On failure `currentHostPort`
        // points at a container that may already be gone, so the next
        // iteration's `waitForPing` would block until its ~30s timeout.
        // Signal the main loop to bail out cleanly instead — SIGINT
        // stays the user-initiated termination path.
        logger.error(
          `Reload failed: ${err instanceof Error ? err.message : String(err)}. ` +
            'The previous container may already be torn down; exiting --watch loop. ' +
            'Re-run cdkl invoke-agentcore --watch to recover.'
        );
        reloadFailed = true;
        currentAbort?.abort();
      }
    });
    reloadChain = next.catch((err) => {
      logger.error(`Reload chain threw: ${err instanceof Error ? err.message : String(err)}`);
    });
  });

  try {
    let firstIteration = true;
    // Outer loop: each iteration opens one WS session. The loop only
    // ends on a benign agent-close (no pending reload) — SIGINT runs
    // the outer command's cleanup + `process.exit`, so this loop is
    // not the termination path on user-initiated shutdown.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (reloadFailed) break;
      await waitForPing(args.containerHost, currentHostPort);
      if (firstIteration) {
        logger.info(
          args.interactive
            ? 'Opening the agent /ws WebSocket (interactive — stdin lines = follow-up frames; Ctrl-D to end)...'
            : 'Opening the agent /ws WebSocket and streaming frames...'
        );
        firstIteration = false;
      } else {
        logger.info(
          args.interactive
            ? 'Re-opening the agent /ws WebSocket (interactive) against the rebuilt container...'
            : 'Re-opening the agent /ws WebSocket against the rebuilt container...'
        );
      }
      const abort = new AbortController();
      currentAbort = abort;
      pendingReload = false;
      const frameSource = args.interactive ? readStdinLines() : undefined;
      try {
        const result = await wsInvoker(args.containerHost, currentHostPort, args.event, {
          sessionId: args.sessionId,
          timeoutMs: args.timeoutMs,
          onMessage: wrapWsOnMessage((text) => process.stdout.write(text), args.interactive),
          abortSignal: abort.signal,
          ...(args.authorization && { authorization: args.authorization }),
          ...(frameSource && { frameSource }),
        });
        // Settle so container logs flush before the next iteration / exit.
        await new Promise((r) => setTimeout(r, 250));
        emitWsResult(result);
      } finally {
        currentAbort = undefined;
      }
      if (!pendingReload) {
        // Agent closed naturally — drain the reload chain (in case a
        // late firing landed) and exit the loop. SIGINT remains the
        // termination path the outer command relies on for shutdown.
        await reloadChain.catch(() => undefined);
        if (!pendingReload) break;
      }
      // Wait for the reload to finish before re-opening. The reload
      // updates `currentHostPort` / `currentStacks` in place.
      await reloadChain.catch(() => undefined);
    }
  } finally {
    try {
      await watcher.close();
    } catch (err) {
      logger.warn(`Watcher close failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
}

/**
 * Issue #255 — soft-reload the agent container in place. `docker cp`
 * the freshly-synthed asset directory contents into the running
 * container's WORKDIR + `docker restart` the container.
 *
 * - WORKDIR is resolved from the live container's image config via
 *   `docker inspect --format '{{.Config.WorkingDir}}'`. An empty
 *   WORKDIR (Docker runtime default) maps to `/`. For
 *   CodeConfiguration runtimes the generated Dockerfile sets
 *   `WORKDIR /app`, so the copy lands there.
 * - The trailing `/.` on the source ensures CONTENTS are copied
 *   (not the directory itself); the trailing `/` on the dest forces
 *   docker cp to treat it as a directory.
 * - `docker restart` cycles PID 1 — the new source is picked up by
 *   the interpreted-language runtime on its next startup. The
 *   container ID + host port + network are preserved across the
 *   restart, so the surrounding watch loop's host-port reference
 *   stays valid.
 *
 * @internal — exported for unit tests of the docker-cp + docker-restart
 * shape without standing up a real container.
 */
const execFileAsync = promisify(execFileCb);

// Stringify a child_process / execFile rejection. `err.stderr` is where
// docker writes its actionable diagnostics; without it the wrapped error
// only carries the generic "Command failed with exit code N" message.
function describeExecError(err: unknown): string {
  if (!(err instanceof Error)) return String(err);
  const stderr = (err as { stderr?: unknown }).stderr;
  const stderrText =
    typeof stderr === 'string'
      ? stderr.trim()
      : stderr instanceof Buffer
        ? stderr.toString('utf8').trim()
        : '';
  return stderrText ? `${err.message}\n${stderrText}` : err.message;
}

export async function softReloadAgentContainer(
  containerId: string,
  newAssetSourceDir: string
): Promise<void> {
  const logger = getLogger();
  const dockerCmd = getDockerCmd();

  // Resolve the runtime WORKDIR. Empty -> `/` (Docker runtime default).
  let workdir: string;
  try {
    const { stdout } = await execFileAsync(dockerCmd, [
      'inspect',
      '--format',
      '{{.Config.WorkingDir}}',
      containerId,
    ]);
    workdir = stdout.trim() || '/';
  } catch (err) {
    throw new CdkLocalError(
      `softReloadAgentContainer: docker inspect of container '${containerId}' failed: ` +
        `${describeExecError(err)}.`,
      'LOCAL_INVOKE_AGENTCORE_WATCH_SOFT_RELOAD_INSPECT_FAILED'
    );
  }
  const workdirDest = workdir.endsWith('/') ? workdir : `${workdir}/`;
  logger.info(
    `Soft-reload: docker cp ${newAssetSourceDir} -> ${containerId}:${workdirDest}; restart.`
  );
  try {
    await execFileAsync(
      dockerCmd,
      ['cp', `${newAssetSourceDir}/.`, `${containerId}:${workdirDest}`],
      {
        maxBuffer: 64 * 1024 * 1024,
      }
    );
  } catch (err) {
    throw new CdkLocalError(
      `softReloadAgentContainer: docker cp into '${containerId}:${workdir}' failed: ` +
        `${describeExecError(err)}.`,
      'LOCAL_INVOKE_AGENTCORE_WATCH_SOFT_RELOAD_CP_FAILED'
    );
  }
  try {
    await execFileAsync(dockerCmd, ['restart', containerId]);
  } catch (err) {
    throw new CdkLocalError(
      `softReloadAgentContainer: docker restart of '${containerId}' failed: ` +
        `${describeExecError(err)}.`,
      'LOCAL_INVOKE_AGENTCORE_WATCH_SOFT_RELOAD_RESTART_FAILED'
    );
  }
}

/**
 * Issue #255 — build the per-firing classifier context for the agent
 * container. Mirrors the ECS service emulator's `loadAssetContextForTarget`
 * shape: returns `undefined` (and the classifier defaults to `'rebuild'`)
 * when the runtime's image is not a CDK docker-image asset, or when the
 * asset manifest lookup misses for either the OLD or NEW synth.
 *
 * For a CodeConfiguration (`fromCodeAsset`) runtime we treat the bundle's
 * `codeAssetHash` as the asset hash + the staged source directory as
 * `newAssetSourceDir`, and synthesize a `Dockerfile` basename that never
 * matches a real file (the generated Dockerfile lives in a build tmpdir,
 * not the source tree, so chokidar can't observe an edit to it). A
 * `fromS3` bundle has no local source tree, so it returns `undefined` and
 * the classifier defaults to rebuild.
 *
 * Container artifacts go through the same `AssetManifestLoader` lookup
 * the ECS path uses, then read `source.directory` + `source.dockerFile`
 * off the matched docker-image entry.
 *
 * @internal — exported for the watch loop's classifier dispatch test.
 */
export async function loadAgentCoreAssetContext(args: {
  /** Original CDK target string the command resolved (display path / logical id). */
  resolvedTarget: string;
  resolved: ResolvedAgentCoreRuntime;
  stacks: StackInfo[];
  cdkOutDir: string;
  assetLoader: AssetManifestLoader;
  oldAssetHash: string | undefined;
}): Promise<ReloadAssetContext | undefined> {
  const { resolvedTarget, resolved, stacks, cdkOutDir, assetLoader, oldAssetHash } = args;
  // Resolve the freshly-synthed candidate stack + runtime so the
  // classifier reads the NEW asset hash, not the boot-time descriptor.
  const newCandidate = pickAgentCoreCandidateStack(resolvedTarget, stacks);
  if (!newCandidate) return undefined;
  // CodeConfiguration (managed runtime) path. `codeAssetHash` is the
  // bundle's file-asset hash; the source dir is the staged asset
  // directory under cdk.out. A `fromS3` bundle has no local source tree
  // — bail out + rebuild.
  if (resolved.codeArtifact) {
    if (resolved.codeArtifact.s3Source) return undefined;
    const manifest = await assetLoader.loadManifest(cdkOutDir, newCandidate.stackName);
    if (!manifest) return undefined;
    const fileAssets = assetLoader.getFileAssets(manifest);
    const asset = fileAssets.get(resolved.codeArtifact.codeAssetHash);
    if (!asset) return undefined;
    const sourceDir = assetLoader.getAssetSourcePath(cdkOutDir, asset);
    return {
      ...(oldAssetHash !== undefined && { oldAssetHash }),
      newAssetHash: resolved.codeArtifact.codeAssetHash,
      newAssetSourceDir: sourceDir,
      // The Dockerfile is generated inside `buildAgentCoreCodeImage`'s
      // build tmpdir, not the source tree, so chokidar can never report
      // an edit to it. Use a sentinel basename that can't collide with
      // anything in the user's source tree (the leading `.` plus the
      // `cdkl-` prefix), so the classifier's `basename === dockerFile`
      // check is effectively dead — interpreted-language source edits
      // route to soft-reload, every other rebuild trigger still works.
      dockerFile: '.cdkl-agentcore-generated-Dockerfile',
    };
  }
  // Container artifact path. The container URI must match a cdk.out
  // docker-image asset entry; otherwise the image is a deployed-registry
  // pin and the classifier rebuild-default is the right answer.
  if (resolved.containerUri === undefined) return undefined;
  const manifest = await assetLoader.loadManifest(cdkOutDir, newCandidate.stackName);
  if (!manifest) return undefined;
  const dockerImageEntry = getDockerImageBySourceHash(manifest, resolved.containerUri);
  if (!dockerImageEntry) return undefined;
  const newAssetHash = dockerImageEntry.hash;
  const newDockerImage = dockerImageEntry.asset;
  if (!newDockerImage.source.directory) return undefined;
  const newAssetSourceDir = path.resolve(cdkOutDir, newDockerImage.source.directory);
  return {
    ...(oldAssetHash !== undefined && { oldAssetHash }),
    newAssetHash,
    newAssetSourceDir,
    dockerFile: path.basename(newDockerImage.source.dockerFile ?? 'Dockerfile'),
  };
}

/**
 * Helper: derive the current asset hash from the live (pre-reload)
 * stacks for the classifier's `oldAssetHash` field. Returns `undefined`
 * when the runtime's image is not resolvable as a CDK asset against the
 * old stacks — the classifier defaults to rebuild in that case, which
 * is the right answer.
 */
/**
 * Async helper: derive the OLD (pre-reload) asset hash for the
 * classifier's `oldAssetHash` field. Code-artifact runtimes carry the
 * hash on the boot-time `resolved.codeArtifact.codeAssetHash`; container
 * runtimes need a manifest lookup against the previous-synth stacks to
 * extract it. Returns `undefined` when the OLD hash can't be derived
 * (image is not a CDK asset, manifest unreadable, no candidate stack) —
 * the classifier treats `undefined` as "force rebuild", which is the
 * conservative default.
 */
/**
 * Resolve the CURRENT (pre-reload) asset hash for the runtime, so the per-firing
 * classifier can compare it against the freshly-synthed hash. Exported so the
 * `cdkl start-agentcore --watch` reload watcher (issue #454, slice 4b) reuses
 * the SAME classification inputs as `invoke-agentcore --ws --watch`.
 */
export async function deriveOldAssetHash(args: {
  resolvedTarget: string;
  resolved: ResolvedAgentCoreRuntime;
  stacks: StackInfo[];
  cdkOutDir: string;
  assetLoader: AssetManifestLoader;
}): Promise<string | undefined> {
  const { resolvedTarget, resolved, stacks, cdkOutDir, assetLoader } = args;
  if (resolved.codeArtifact) return resolved.codeArtifact.codeAssetHash;
  if (resolved.containerUri === undefined) return undefined;
  const candidate = pickAgentCoreCandidateStack(resolvedTarget, stacks);
  if (!candidate) return undefined;
  const manifest = await assetLoader.loadManifest(cdkOutDir, candidate.stackName);
  if (!manifest) return undefined;
  const entry = getDockerImageBySourceHash(manifest, resolved.containerUri);
  return entry?.hash;
}

export function createLocalInvokeAgentCoreCommand(
  opts: CreateLocalInvokeAgentCoreCommandOptions = {}
): Command {
  setEmbedConfig(opts.embedConfig);
  const cmd = new Command('invoke-agentcore')
    .description(
      'Run a Bedrock AgentCore Runtime container locally and invoke it once over its protocol ' +
        'contract: HTTP (POST /invocations + GET /ping on 8080; SSE / WebSocket are HTTP wire-shape ' +
        'variants on the same port), MCP (POST /mcp Streamable HTTP on 8000), A2A, or AGUI. ' +
        'Resolves the AWS::BedrockAgentCore::Runtime, pulls/builds its container, injects ' +
        'env vars + AWS credentials, and prints the response. For an MCP runtime, runs the session ' +
        'handshake then sends one JSON-RPC request (tools/list by default, or the method/params from ' +
        '--event). Target accepts a CDK display path (MyStack/MyAgent) or stack-qualified logical ID ' +
        '(MyStack:MyAgentRuntime1234). Single-stack apps may omit the stack prefix. ' +
        'Omit <target> in an interactive terminal to pick from a list. ' +
        'Supports the container artifact and the CodeConfiguration managed-runtime artifact ' +
        '(fromCodeAsset, built from source) on all four protocols; the agent calls real AWS for managed services.'
    )
    .argument(
      '[target]',
      'CDK display path or stack-qualified logical ID of the AgentCore Runtime to invoke (omit to pick interactively in a TTY)'
    )
    .action(
      withErrorHandling(
        async (target: string | undefined, options: LocalInvokeAgentCoreOptions) => {
          await localInvokeAgentCoreCommand(target, options, opts.extraStateProviders);
        }
      )
    );

  addInvokeAgentCoreSpecificOptions(cmd);
  [...commonOptions(), ...appOptions(), ...contextOptions].forEach((opt) => cmd.addOption(opt));
  cmd.addOption(regionOption);
  return cmd;
}

/**
 * Register the option block that `cdkl invoke-agentcore` adds on top of
 * the shared common / app / context option helpers. Shared between
 * `cdkl invoke-agentcore` and any host CLI (e.g. cdkd's
 * `local invoke-agentcore`) that wraps the single-shot AgentCore Runtime
 * container runner, so adding or renaming an `invoke-agentcore`-only flag
 * here propagates to every embedder without duplicate `.addOption(...)`
 * blocks.
 *
 * Calling order only affects `--help` presentation (Commander parses
 * insertion-order-independent). The host-CLI convention is host-specific
 * options first, then this helper, then the shared common / app / context
 * options — host flags / invoke-agentcore flags / common flags grouped
 * in three `--help` clusters. Chainable: returns `cmd`.
 */
export function addInvokeAgentCoreSpecificOptions(cmd: Command): Command {
  return cmd
    .addOption(new Option('-e, --event <file>', 'JSON event payload file (default: {})'))
    .addOption(new Option('--event-stdin', 'Read event JSON from stdin').default(false))
    .addOption(
      new Option(
        '--env-vars <file>',
        'JSON env-var overrides (SAM-compatible: {"LogicalId":{"KEY":"VALUE"}, "Parameters": {...}})'
      )
    )
    .addOption(
      new Option(
        '--session-id <id>',
        'AgentCore runtime session id header value (default: a random UUID)'
      )
    )
    .addOption(
      new Option(
        '--ws',
        "Stream over the HTTP-protocol agent's bidirectional /ws WebSocket endpoint (on 8080) " +
          'instead of POST /invocations: send --event as the first frame and print every received ' +
          'frame to stdout until the agent closes. ' +
          'When stdin is a TTY, additionally reads stdin lines as follow-up text frames ' +
          '(multi-turn REPL; Ctrl-D to end). When stdin is piped / redirected / non-TTY (CI), ' +
          'only the initial --event frame is sent (one-shot). Force one-shot in a TTY with ' +
          '`cdkl ... --ws </dev/null`. Ignored for an MCP runtime.'
      ).default(false)
    )
    .addOption(
      new Option(
        '--bearer-token <jwt>',
        'Bearer JWT this command SUPPLIES (the supplier role) when the runtime declares a ' +
          'customJwtAuthorizer — `cdkl invoke-agentcore` is the local-dev client making the ' +
          'outbound call, so it always presents this token to its own invocation. Verified ' +
          "against the runtime's OIDC discovery URL (signature / issuer / expiry / audience) " +
          'before the container starts, then forwarded to /invocations as Authorization: ' +
          'Bearer <jwt>. ' +
          'Contrast with `cdkl start-alb --bearer-token`, where the role is reversed — the ' +
          'ALB front-door RECEIVES inbound requests and injects this token only as a default ' +
          'when an inbound request has none (default-when-missing).'
      )
    )
    .addOption(
      new Option(
        '--no-verify-auth',
        'Skip inbound JWT verification even when the runtime declares a customJwtAuthorizer ' +
          '(local-dev escape hatch). A --bearer-token, if given, is still forwarded.'
      )
    )
    .addOption(
      new Option(
        '--sigv4',
        'Sign the /invocations POST with AWS SigV4 (service bedrock-agentcore) using the resolved ' +
          'credentials, matching the cloud default when the runtime declares no customJwtAuthorizer. ' +
          'Opt-in: default unsigned. Mutually exclusive with --bearer-token; ignored on a JWT-protected runtime.'
      ).default(false)
    )
    .addOption(
      new Option(
        '--platform <platform>',
        'docker --platform for the agent container (linux/amd64 or linux/arm64). ' +
          'Defaults to linux/arm64 because the cloud AgentCore Runtime requires arm64. ' +
          'Override to linux/amd64 only when iterating against an amd64 dev container locally; ' +
          'note the image will not run on the cloud runtime as-is.'
      )
        .choices(['linux/amd64', 'linux/arm64'])
        .default('linux/arm64')
    )
    .addOption(
      new Option(
        '--no-pull',
        'Skip docker pull (use cached image) — no-op for the local-build path'
      )
    )
    .addOption(
      new Option(
        '--no-build',
        'Skip docker build on the local-asset path (use the previously-built tag). No-op for the ECR / registry pull paths.'
      )
    )
    .addOption(
      new Option(
        '--container-host <host>',
        'Host IP the host uses to bind the agent port to. Must be a numeric IP (Docker rejects hostnames here). Defaults to 127.0.0.1.'
      ).default('127.0.0.1')
    )
    .addOption(
      new Option(
        '--timeout <ms>',
        'Per-request timeout in milliseconds. Applied to POST /invocations, POST /mcp, and the ' +
          '/ws open-to-close window. Raise this for long-running agent calls that exceed the default.'
      )
        .default(120000)
        .argParser(parseTimeoutMs)
    )
    .addOption(
      new Option(
        '--assume-role [arn]',
        "Assume the runtime's execution role and forward STS-issued temp credentials to the container " +
          'so the agent runs with the deployed role. Three forms: ' +
          '(1) `--assume-role <arn>` assumes the explicit ARN; ' +
          "(2) `--assume-role` (bare) uses the runtime's RoleArn when it is a literal ARN; " +
          '(3) `--no-assume-role` opts out. ' +
          "Off by default — the developer's shell credentials are forwarded unchanged."
      )
    )
    .addOption(
      new Option(
        '--ecr-role-arn <arn>',
        'Role ARN to assume before authenticating against ECR for cross-account / centralized registries. ' +
          'Same-account / same-region pulls do not need this flag.'
      )
    )
    .addOption(
      new Option(
        '--from-cfn-stack [cfn-stack-name]',
        'Read a deployed CloudFormation stack via ListStackResources and substitute Ref / Fn::ImportValue ' +
          'in env vars with the deployed physical IDs / exports. Bare form uses the resolved stack name; ' +
          'pass an explicit value when the CFn stack name differs.'
      )
    )
    .addOption(
      new Option(
        '--stack-region <region>',
        'Region of the state record to read. Used with --from-cfn-stack as the CFn client region.'
      )
    )
    .addOption(
      new Option(
        '--watch',
        'Re-synth and reload the agent container on CDK source changes. Only meaningful with the ' +
          'long-running --ws session path; single-shot POST /invocations, ' +
          'MCP, and A2A invocations run once and exit, so --watch is logged as a no-op WARN for them ' +
          'and the single shot proceeds. The active /ws socket is closed cleanly on every reload firing ' +
          'so the next session connects to the rebuilt container — the honest local-dev semantic.'
      ).default(false)
    );
}
